/* eslint-disable eqeqeq */
import * as Yup from "yup";
import { parse, isAfter, isValid, isEqual, isBefore } from "date-fns";
import {
  isColor,
  isBackground,
  validateDate,
  isDateAfterToday,
  isValidMarginFormat,
  generatePassword,
  generateSixDigitCode,
  convertDateToDisplay,
  defaultCreationDate,
  defaultEndDate,
  defaultEndDateAgenda,
} from "../assets/functions.js";
import { createSocialSection } from "../utils/socialSection.js";
import { domainConfig } from "../config.js";
import {
  activityAvailabilityValidationSchema,
  createActivityAvailability,
} from "./apps/ActivityAvailability.js";

export const jsonComponents = (value) => {
  let data = "";
  switch (value) {
    case "expositores":
      data = {
        name: "",
        description: "",
        logo: "",
        location: "",
        media: [],
        about: "",
        social_media: [],
        categories: [],
        quiz: null,
        quiz_tips: [],
      };
      break;
    case "palestrantes":
      data = {
        name: "",
        role: "",
        image: "",
        company: "",
        about: "",
        social_media: [],
      };
      break;
    case "patrocinadores":
      data = {
        name: "",
        category: "",
        image: "",
      };
      break;
    case "agenda":
      data = {
        title: "", //input
        location: "", //input
        showEndDate: true, //checkbox
        showInterest: true, //checkbox
        about: "", //textarea
        files: [], //skip for now
        activities: [], //skip for now
        liveSession: [], //skip for now
        startDate: convertDateToDisplay(defaultCreationDate()), //input
        endDate: convertDateToDisplay(defaultEndDateAgenda()), //input
        timeLimit: convertDateToDisplay(defaultEndDateAgenda()), //input
        vacancySystem: {
          status: false, //checkbox
          slots: 25, //number step 1
        },
      };
      break;

    default:
      break;
  }
  return data;
};

const getQuizCorrectAnswerCount = (answers = []) => (
  Array.isArray(answers)
    ? answers.filter((answer) => answer?.isCorrect === true).length
    : 0
);

const getQuizAnswerCount = (answers = []) => (
  Array.isArray(answers) ? answers.length : 0
);

const validateQuizMultipleChoiceLimits = function (question) {
  if (!question || question.enableMultipleChoice !== true || ![1, 2].includes(Number(question.exercise))) {
    return true;
  }

  const answersCount = getQuizAnswerCount(question.answers);
  const min = Number(question.multipleChoiceMin);
  const max = Number(question.multipleChoiceMax);

  if (!Number.isInteger(min) || !Number.isInteger(max)) {
    return this.createError({
      path: `${this.path}.multipleChoiceMin`,
      message: "Informe limites válidos para múltiplas respostas",
    });
  }

  if (min < 1 || max < min || max > answersCount) {
    return this.createError({
      path: `${this.path}.multipleChoiceMin`,
      message: "Configure limites válidos para as respostas",
    });
  }

  if (Number(question.exercise) === 1) {
    const correctAnswerCount = getQuizCorrectAnswerCount(question.answers);
    if (correctAnswerCount <= 0) {
      return true;
    }

    if (correctAnswerCount < min || correctAnswerCount > max) {
      return this.createError({
        path: `${this.path}.multipleChoiceMin`,
        message: "As respostas corretas devem respeitar os limites de múltiplas respostas",
      });
    }

    return true;
  }

  return true;
};

export const schemaComponents = (value, dataTable) => {
  let schema = {};

  switch (value) {
    case "agenda":
      schema = Yup.object().shape({
        title: Yup.string()
          .required("Campo obrigatório")
          .max(100, "Máximo de 100 caracteres"),
        location: Yup.string()
          .max(150, "Máximo de 150 caracteres")
          .nullable(),
        about: Yup.string().max(1000, "Máximo de 1000 caracteres").nullable(),
        startDate: Yup.string()
          .required("Campo obrigatório")
          .test("date", "Data inválida", function (valueX) {
            if (validateDate(valueX)) {
              return true;
            } else {
              return false;
            }
          })
          .test(
            "date-within-event-range",
            "Data de início deve estar entre a data de criação e a data de término do evento",
            function (valueX) {
              if (
                dataTable &&
                dataTable.creation_date &&
                dataTable.end_date &&
                valueX &&
                validateDate(valueX)
              ) {
                const startDateParsed = parse(
                  valueX,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );
                const eventStartParsed = parse(
                  dataTable.creation_date,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );
                const eventEndParsed = parse(
                  dataTable.end_date,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );

                if (
                  isValid(startDateParsed) &&
                  isValid(eventStartParsed) &&
                  isValid(eventEndParsed)
                ) {
                  return (
                    (isAfter(startDateParsed, eventStartParsed) ||
                      isEqual(startDateParsed, eventStartParsed)) &&
                    (isBefore(startDateParsed, eventEndParsed) ||
                      isEqual(startDateParsed, eventEndParsed))
                  );
                }
              }
              return true;
            },
          ),
        endDate: Yup.string()
          .required("Campo obrigatório")
          .test("date", "Data inválida", function (valueX) {
            if (validateDate(valueX)) {
              return true;
            } else {
              return false;
            }
          })
          .test(
            "date-within-event-range",
            "Data de término deve estar entre a data de criação e a data de término do evento",
            function (valueX) {
              if (
                dataTable &&
                dataTable.creation_date &&
                dataTable.end_date &&
                valueX &&
                validateDate(valueX)
              ) {
                const endDateParsed = parse(
                  valueX,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );
                const eventStartParsed = parse(
                  dataTable.creation_date,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );
                const eventEndParsed = parse(
                  dataTable.end_date,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );

                if (
                  isValid(endDateParsed) &&
                  isValid(eventStartParsed) &&
                  isValid(eventEndParsed)
                ) {
                  return (
                    (isAfter(endDateParsed, eventStartParsed) ||
                      isEqual(endDateParsed, eventStartParsed)) &&
                    (isBefore(endDateParsed, eventEndParsed) ||
                      isEqual(endDateParsed, eventEndParsed))
                  );
                }
              }
              return true;
            },
          )
          .test(
            "date-after-start",
            "Data de término deve ser posterior à data de início",
            function (valueX) {
              const { startDate } = this.parent;
              if (
                startDate &&
                valueX &&
                validateDate(startDate) &&
                validateDate(valueX)
              ) {
                const startDateParsed = parse(
                  startDate,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );
                const endDateParsed = parse(
                  valueX,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );

                if (isValid(startDateParsed) && isValid(endDateParsed)) {
                  return (
                    isAfter(endDateParsed, startDateParsed) ||
                    isEqual(endDateParsed, startDateParsed)
                  );
                }
              }
              return true;
            },
          ),
        timeLimit: Yup.string().when(["vacancySystem.status"], {
          is: true,
          then: Yup.string()
            .test("date", "Data inválida", function (valueX) {
              if (!valueX) return true; // timeLimit is optional
              if (validateDate(valueX)) {
                return true;
              } else {
                return false;
              }
            })
            .test(
              "date-within-event-dates",
              "Data limite deve estar entre a data de criação e a data de término do evento",
              function (valueX) {
                if (
                  dataTable &&
                  dataTable.creation_date &&
                  dataTable.end_date &&
                  valueX &&
                  validateDate(dataTable.creation_date) &&
                  validateDate(dataTable.end_date) &&
                  validateDate(valueX)
                ) {
                  const timeLimitParsed = parse(
                    valueX,
                    "dd/MM/yyyy HH:mm",
                    new Date(),
                  );
                  const startDateParsed = parse(
                    dataTable.creation_date,
                    "dd/MM/yyyy HH:mm",
                    new Date(),
                  );
                  const endDateParsed = parse(
                    dataTable.end_date,
                    "dd/MM/yyyy HH:mm",
                    new Date(),
                  );

                  if (
                    isValid(timeLimitParsed) &&
                    isValid(startDateParsed) &&
                    isValid(endDateParsed)
                  ) {
                    return (
                      (isAfter(timeLimitParsed, startDateParsed) ||
                        isEqual(timeLimitParsed, startDateParsed)) &&
                      (isBefore(timeLimitParsed, endDateParsed) ||
                        isEqual(timeLimitParsed, endDateParsed))
                    );
                  }
                }
                return true;
              },
            ),
          otherwise: Yup.string().nullable(),
        }),
        showEndDate: Yup.boolean(),
        showInterest: Yup.boolean(),
        vacancySystem: Yup.object().shape({
          status: Yup.boolean(),
          slots: Yup.number().when("status", {
            is: true,
            then: Yup.number()
              .integer("Deve ser um número inteiro")
              .min(1, "Deve ser maior que 0")
              .required(
                "Campo obrigatório quando sistema de vagas está ativado",
              ),
            otherwise: Yup.number().nullable(),
          }),
        }),
        gallery: Yup.array().max(8, "Máximo de 8 imagens na galeria"),
      });
      break;

    case "expositores":
      {
        const quizTipsRequired =
          !!dataTable?.json?.configs?.exhibitor?.enableCollection &&
          !!dataTable?.json?.configs?.exhibitor?.enableQuizTips;
        const requiredQuizActivityIds = Array.isArray(
          dataTable?.json?.configs?.exhibitor?.quizActivityIds,
        )
          ? [
            ...new Set(
              dataTable.json.configs.exhibitor.quizActivityIds
                .map((id) => Number(id))
                .filter((id) => Number.isInteger(id) && id > 0),
            ),
          ]
          : [];

        schema = Yup.object().shape({
          name: Yup.string()
            .required("Campo obrigatório")
            .max(100, "Máximo de 100 caracteres"),
          description: Yup.string()
            .max(150, "Máximo de 150 caracteres")
            .nullable(),
          logo: Yup.string().required("Campo obrigatório"),
          location: Yup.string()
            .max(150, "Máximo de 150 caracteres")
            .nullable(),
          about: Yup.string().max(1000, "Máximo de 1000 caracteres").nullable(),
          categories: Yup.string().nullable(),
          social_media: Yup.array().of(
            Yup.object().shape({
              name: Yup.string().required("Nome da rede social é obrigatório"),
              url: Yup.string().url("URL inválida").required("URL é obrigatória"),
            }),
          ),
          media: Yup.array(),
          quiz_tips: Yup.array()
            .of(
              Yup.object().shape({
                activity_id: Yup.number().nullable(),
                question_id: Yup.number().nullable(),
              }),
            )
            .test(
              "required-quiz-tip-per-selected-quiz",
              "Selecione uma questão para cada quiz habilitado na dinâmica do expositor.",
              function (value) {
                if (!quizTipsRequired || requiredQuizActivityIds.length === 0) {
                  return true;
                }

                const normalizedQuizTips = Array.isArray(value)
                  ? value
                    .map((tip) => ({
                      activity_id: Number(tip?.activity_id),
                      question_id: Number(tip?.question_id),
                    }))
                    .filter(
                      (tip) =>
                        Number.isInteger(tip.activity_id) &&
                        tip.activity_id > 0 &&
                        Number.isInteger(tip.question_id) &&
                        tip.question_id > 0,
                    )
                  : [];

                const selectedActivityIds = new Set(
                  normalizedQuizTips.map((tip) => tip.activity_id),
                );

                return requiredQuizActivityIds.every((activityId) =>
                  selectedActivityIds.has(activityId),
                );
              },
            ),
        });
        break;
      }

    case "palestrantes":
      schema = Yup.object().shape({
        name: Yup.string()
          .required("Campo obrigatório")
          .max(100, "Máximo de 100 caracteres"),
        role: Yup.string()
          .required("Campo obrigatório")
          .max(75, "Máximo de 75 caracteres"),
        image: Yup.string().required("Campo obrigatório"),
        company: Yup.string().max(75, "Máximo de 75 caracteres").nullable(),
        about: Yup.string().max(1000, "Máximo de 1000 caracteres").nullable(),
        social_media: Yup.array().of(
          Yup.object().shape({
            name: Yup.string().required("Nome da rede social é obrigatório"),
            url: Yup.string().url("URL inválida").required("URL é obrigatória"),
          }),
        ),
      });
      break;
    case "pontos-de-interesse":
      schema = Yup.object().shape({
        title: Yup.string()
          .required("Campo obrigatório")
          .max(100, "Máximo de 100 caracteres"),
        image: Yup.string().required("Campo obrigatório"),
        tip: Yup.string().max(150, "Máximo de 150 caracteres").nullable(),
        score_id: Yup.number().integer().positive().nullable(),
      });
      break;
    case "patrocinadores":
      schema = Yup.object().shape({
        name: Yup.string()
          .required("Campo obrigatório")
          .max(100, "Máximo de 100 caracteres"),
        category: Yup.string()
          .max(250, "Máximo de 250 caracteres")
          .nullable(),
        image: Yup.string().required("Campo obrigatório"),
        tier_id: Yup.string().required("Nível do patrocinador é obrigatório"),
        link: Yup.string()
          .nullable()
          .test(
            "url-validation",
            "Por favor, insira uma URL válida (deve começar com http:// ou https://)",
            function (value) {
              if (!value || value.trim() === "") {
                return true; // Campo opcional
              }
              try {
                const urlObj = new URL(value);
                return (
                  urlObj.protocol === "http:" || urlObj.protocol === "https:"
                );
              } catch {
                return false;
              }
            },
          ),
      });
      break;

    case "atividades":
      schema = Yup.object().shape({
        title: Yup.string()
          .required("Campo obrigatório")
          .max(100, "Máximo de 100 caracteres"),
        json: Yup.object().shape({
          availability: activityAvailabilityValidationSchema(dataTable),
          questions: Yup.array()
            .min(1, "Adicione pelo menos uma questão")
            .of(
              Yup.object()
                .shape({
                  question: Yup.string().required("Campo obrigatório"),
                  exercise: Yup.number(),
                  value: Yup.number().integer("Use um número inteiro").min(0, "Use zero ou mais"),
                  enableMultipleChoice: Yup.boolean(),
                  multipleChoiceMin: Yup.number(),
                  multipleChoiceMax: Yup.number(),
                  answers: Yup.array(),
                })
                .test(
                  "multiple-choice-correct-answer",
                  "Selecione pelo menos uma resposta correta",
                  function (question) {
                    if (Number(question?.exercise) !== 1) {
                      return true;
                    }

                    const hasCorrectAnswer = Array.isArray(question?.answers)
                      && question.answers.some((answer) => answer?.isCorrect === true);

                    return hasCorrectAnswer || this.createError({
                      path: `${this.path}.correctAnswer`,
                      message: "Selecione pelo menos uma resposta correta",
                    });
                  },
                )
                .test(
                  "multiple-choice-limits",
                  "Configure limites válidos para múltiplas respostas",
                  validateQuizMultipleChoiceLimits,
                ),
            ),
        }),
      });
      break;

    default:
      schema = Yup.object().shape({});
      break;
  }

  return schema;
};

export const jsonCustomizacao = (aplicativoID) => {
  let data = "";
  switch (aplicativoID) {
    case 1:
      data = {
        availability: createActivityAvailability(),
        sound: false,
        showScore: true,
        showRanking: true,
        timer: {
          status: true,
          value: 60,
        },
        reset: {
          status: false,
          text: "Finalizar",
        },
        retry: {
          status: true,
          text: "Jogar novamente",
        },
        endText: {
          title: {
            status: true,
            value: "Parabéns! Você completou o Desafio",
          },
          description: {
            status: false,
            value: "",
          },
          showImage: {
            status: false,
            value: "",
            width: "250",
          },
          titleWinner: {
            status: true,
            value: "Parabéns! Você completou o Desafio",
          },
          descriptionWinner: {
            status: false,
            value: "",
          },
        },
        apresentation: {
          rankingPage: {
            title: {
              status: true,
              value: "Ranking",
            },
          },
          configs: {
            logoSize: 380,
          },
          imageSide: {
            value: 0,
            position: "start",
            background: "",
          },
          textSide: {
            value: 100,
            backgroundColor: "rgba(255,255,255,1)",
            textColor: "rgba(0,0,0,1)",
            h5: 1.25,
            p: 1,
          },
          showQRCode: {
            text: "Leia o QRCode para participar",
            link: "",
            status: true,
            position: {
              x: "left",
              y: "bottom",
            },
          },
        },
        memoryGame: {
          cards: {
            value: [
              {
                pair: 1,
                url: "",
              },
              {
                pair: 1,
                url: "",
              },
            ],
            front: "",
            borderRadius: 0,
            width: 225,
            cardsPerRow: 3,
            padding: 10,
            firstPlayerColor: "rgba(255,255,255,1)",
            firstPlayerBackgroundColor: "darkgray",
            secondPlayerColor: "rgba(255,255,255,1)",
            secondPlayerBackgroundColor: "black",
          },
          allowTwoPlayers: {
            status: true,
            description: "Selecione um modo de jogo",
          },
          endGameTimer: 3000,
          flipCardTimer: 1500,
          gameText: {
            status: false,
            value: "",
          },
        },
      };
      return data;
    case 2:
      data = {
        sound: false,
        showRanking: true,
        showScore: true,
        allowPreviousQuestion: false,
        showChart: false,
        shuffleQuestions: false,
        shuffleQuestionsArray: "",
        correctFeedback: true,
        answerFeedback: true,
        correctAnswer: "rgba(0, 153, 68,1)",
        incorrectAnswer: "rgba(242, 58, 58,1)",
        maxWidthGame: 950,
        selectQuestions: {
          status: false,
          value: 5,
        },
        retry: {
          status: true,
          text: "Jogar novamente",
        },
        reset: {
          status: false,
          text: "Finalizar",
        },
        timer: {
          status: true,
          value: 30,
          position: "belowQuestion",
        },
        endText: {
          title: {
            status: true,
            value: "Parabéns! Você completou o Desafio",
          },
          description: {
            status: false,
            value: "",
          },
          showImage: {
            status: false,
            value: "",
            width: "250",
          },
          titleWinner: {
            status: true,
            value: "Parabéns! Você completou o Desafio",
          },
          descriptionWinner: {
            status: false,
            value: "",
          },
        },
        result: [],
        apresentation: {
          rankingPage: {
            title: {
              status: true,
              value: "Ranking",
            },
          },
          configs: {
            logoSize: 380,
          },
          imageSide: {
            value: 0,
            position: "start",
            background: "",
          },
          textSide: {
            value: 100,
            backgroundColor: "rgba(255,255,255,1)",
            textColor: "rgba(0,0,0,1)",
            h5: 1.25,
            p: 1,
          },
          showQRCode: {
            text: "Leia o QRCode para participar",
            link: "",
            status: true,
            position: {
              x: "left",
              y: "bottom",
            },
          },
        },
        questions: [
          {
            question_id: 1,
            exercise: 1,
            type: 1,
            col: 6,
            question: "",
            url: "",
            shuffleAnswers: true,
            value: 0,
            enableMultipleChoice: false,
            multipleChoiceMin: 1,
            multipleChoiceMax: 1,
            additionalAnswer: {
              status: false,
              text: "",
              image: "",
            },
            answers: [
              {
                text: "",
                isCorrect: true,
                order: 0,
              },
              {
                text: "",
                isCorrect: false,
                order: 1,
              },
              {
                text: "",
                isCorrect: false,
                order: 2,
              },
              {
                text: "",
                isCorrect: false,
                order: 3,
              },
            ],
          },
        ],
      };
      return data;
    case 3:
      data = {
        deleteHistory: true,
        showMessageHistory: true,
        maxlength: 255,
        userFeedback: false,
        autoApproval: false,
        messageLimit: 3,
        minlength: 0,
        title: {
          status: true,
          value: "Como Funciona?",
        },
        description: {
          status: true,
          value:
            "1. Escreva Sua Pergunta: No campo abaixo, digite sua pergunta. Pode ser sobre o tema do evento, comentários específicos do apresentador, ou qualquer curiosidade que você tenha.\n\n 2.Envie com um Clique: Após redigir sua pergunta, clique em 'Enviar'. Nossa equipe cuidará da revisão e encaminhamento para o apresentador.\n\n3. Aguarde a Resposta: Se sua pergunta for selecionada, será abordada pelo apresentador durante o evento",
        },
        apresentation: {
          title: {
            status: true,
            value: "Me faça qualquer pergunta!",
          },
          configs: {
            logoSize: 380,
          },
          imageSide: {
            value: 0,
            position: "start",
            background: "",
          },
          textSide: {
            value: 100,
            backgroundColor: "rgba(255,255,255,1)",
            textColor: "rgba(0,0,0,1)",
            fontFamily: "Roboto",
            messageSlider: 2,
            h5: 1.25,
            p: 1,
          },
          showIsAnswered: true,
          showAuthor: true,
          showQRCode: {
            text: "Leia o QRCode para participar",
            link: "",
            status: true,
            position: {
              x: "left",
              y: "bottom",
            },
          },
        },
      };
      return data;
    case 4:
      data = {
        type: 1,
        deleteHistory: true,
        showMessageHistory: true,
        maxlength: 255,
        autoApproval: false,
        userFeedback: false,
        messageLimit: 3,
        minlength: 25,
        title: {
          status: true,
          value: "Como Funciona?",
        },
        description: {
          status: true,
          value: "Escreva sua mensagem para aprecer na tela!",
        },
        reset: {
          status: false,
        },
        apresentation: {
          title: {
            status: false,
            value: "",
          },
          configs: {
            logoSize: 380,
            mode: 1,
            gallery: {
              width: 186,
              height: 219,
              newItemWidth: 392,
              newItemHeight: 458,
              columns: 6,
              rows: 3,
              newItemTotalTime: 5000,
              transitionTime: 5000,
              padding: 1,
              backgroundColor: "rgba(237, 158, 6, 1)",
              color: "rgba(255, 255, 255, 1)",
            },
          },
          imageSide: {
            value: 0,
            position: "start",
            background: "",
          },
          textSide: {
            value: 100,
            backgroundColor: "rgba(255,255,255,1)",
            textColor: "rgba(0,0,0,1)",
            messageSlider: 2,
            fontFamily: "Roboto",
            h5: 1.25,
            p: 1,
          },
          intervalTime: 15000,
          showAuthor: true,
          showIsAnswered: false,
          showQRCode: {
            text: "Leia o QRCode para participar",
            link: "",
            status: true,
            position: {
              x: "left",
              y: "bottom",
            },
          },
        },
      };
      return data;
    case 5:
      data = {
        deleteHistory: true,
        showMessageHistory: true,
        maxlength: 50,
        userFeedback: false,
        autoApproval: false,
        messageLimit: 1,
        maxMessagePerLimit: 3,
        minlength: 0,
        reset: {
          status: false,
          text: "Finalizar",
        },
        selectWords: {
          status: false,
          options: [],
        },
        fallbackWords: {
          status: true,
          options: [],
        },
        title: {
          status: true,
          value: "Como Funciona?",
        },
        description: {
          status: true,
          value:
            "Escolha as palavras que você mais gosta e envie para o apresentador.\n\nVocê pode escolher até 3 palavras.\n\nAs palavras serão exibidas na tela do apresentador.",
        },
        apresentation: {
          title: {
            status: true,
            value: "Me faça qualquer pergunta!",
          },
          configs: {
            logoSize: 380,
          },
          imageSide: {
            value: 0,
            position: "start",
            background: "",
          },
          textSide: {
            value: 100,
            background: {
              type: "color",
              url: "",
              color: "rgba(255,255,255,1)",
              backgroundPosition: "center",
            },
            textColor: "rgba(0,0,0,1)",
            fontFamily: "Roboto",
            h5: 1.25,
            p: 1,
            configs: {
              fontSizeMin: 30,
              fontSizeMax: 80,
              displayTime: 30,
              fontStyle: "normal",
              minFontWeight: 400,
              maxFontWeight: 700,
              minRotation: 0,
              maxRotation: 0,
              spiral: "archimedean",
              transition: "all 0.5s ease-out",
              padding: 2,
              timeInterval: 1,
              maxWords: 100,
              animationDurationMultiplier: 50,
              groupDelay: 500,
              scaleDuration: 300,
              scaleSize: 1.3,
              enableTooltip: false,
              useGradients: false,
            },
          },
          showQRCode: {
            text: "Leia o QRCode para participar",
            link: "",
            status: true,
            position: {
              x: "left",
              y: "bottom",
            },
          },
        },
      };
      return data;
    case 6:
      data = {
        title: {
          status: false,
          value: "",
        },
        description: {
          status: false,
          value: "",
        },
        reset: {
          status: false,
        },
        prizeDrawType: 1,
        drawMode: 1,
        maxDraws: 3,
        showResult: {
          imageWidth: 250,
          drawImage: "",
          drawImageTimeout: 5000,
          winnerImage: "",
          winnerText:
            "Parabéns você foi o ganhador!\n\nSiga até o estande no palco principar para retirar seu prêmio!",
          loserImage: "",
          loserText: "Que pena, você não ganhou!\n\nBoa sorte na próxima",
          waitingImage: "",
          waitingText: "Esperando sorteio...",
        },
        prizes: [],
        apresentation: {
          title: {
            status: true,
            value: "Leitor de QRCode",
          },
          showQRCode: {
            status: true,
            position: {
              x: "right",
              y: "top",
            },
            text: "Leia o QRCode para participar",
            link: "",
          },
          prize: {
            title: {
              status: true,
              value: "Sorteio das premiações",
            },
            image: "",
            width: 300,
          },
          configs: {
            logoSize: 380,
          },
          imageSide: {
            value: 0,
            position: "start",
            background: "",
          },
          textSide: {
            value: 100,
            backgroundColor: "rgba(255,255,255,1)",
            textColor: "rgba(0,0,0,1)",
            h5: 1.25,
            p: 1,
            fontFamily: "Roboto",
          },
        },
      };
      return data;
    case 7:
      data = {
        rouletteType: "wheel",
        unityUrl: "",
        initialScreen: {
          title: "Bem-vindo à Roleta!",
          description: "Clique no botão abaixo para começar",
          buttonText: "Iniciar Roleta",
        },
        endPage: {
          title: "Agradecemos sua participação!",
          buttonText: "Clique na tela para finalizar",
        },
        roulette: {
          isInteractive: false,
          activatePrizes: false,
          nextResult: false,
          spinToCenter: false,
          duration: 3000,
          numberOfRevolutions: 2,
          borderColor: "rgba(0,0,0,1)",
          borderWidth: 1,
          debug: false,
          image: "",
          itemLabelAlign: "right",
          itemLabelBaselineOffset: 0,
          itemLabelFont: "sans-serif",
          itemLabelFontSizeMax: 40,
          itemLabelRadius: 0.85,
          itemLabelRadiusMax: 0,
          itemLabelRotation: 0,
          itemLabelStrokeColor: "rgba(255,255,255,1)",
          itemLabelStrokeWidth: 0,
          lineColor: "rgba(0,0,0,1)",
          lineWidth: 1,
          overlayImage: "",
          pixelRatio: 0,
          pointerAngle: 0,
          radius: 1,
          rotationResistance: -35,
          rotationSpeedMax: 300,
          items: [
            {
              id: "id-fatia01",
              label: "Fatia01",
              possibility: 25,
              prize: "",
              quantity: 10,
              backgroundColor: "red",
              image: null,
              imageOpacity: 1,
              imageRadius: 0.5,
              imageRotation: 1,
              imageScale: 1,
              labelColor: "rgba(255, 255, 255, 1)",
              value: "id-fatia01",
              weight: 1,
              new: false,
            },
            {
              id: "id-fatia02",
              label: "Fatia02",
              possibility: 25,
              prize: "",
              quantity: 10,
              backgroundColor: "blue",
              image: null,
              imageOpacity: 1,
              imageRadius: 0.5,
              imageRotation: 1,
              imageScale: 1,
              labelColor: "rgba(255, 255, 255, 1)",
              value: "id-fatia01",
              weight: 1,
              new: false,
            },
            {
              id: "id-fatia03",
              label: "Fatia03",
              possibility: 25,
              prize: "",
              quantity: 10,
              backgroundColor: "green",
              image: null,
              imageOpacity: 1,
              imageRadius: 0.5,
              imageRotation: 1,
              imageScale: 1,
              labelColor: "rgba(255, 255, 255, 1)",
              value: "id-fatia01",
              weight: 1,
              new: false,
            },
            {
              id: "id-fatia04",
              label: "Fatia04",
              possibility: 25,
              prize: "",
              quantity: 10,
              backgroundColor: "yellow",
              image: null,
              imageOpacity: 1,
              imageRadius: 0.5,
              imageRotation: 1,
              imageScale: 1,
              labelColor: "rgba(255, 255, 255, 1)",
              value: "id-fatia01",
              weight: 1,
              new: false,
            },
          ],
        },
        apresentation: {
          textSide: {
            background: {
              type: "color",
              url: "",
              color: "rgba(255,255,255,1)",
              backgroundPosition: "center",
            },
            textColor: "rgba(0,0,0,1)",
            h5: 1.25,
            p: 1,
          },
          register: {
            title: "Preencha os campos abaixo para se inscrever",
          },
          configs: {
            sound: false,
            logoSize: 380,
            qrcodeEndPage: 30000,
            form: 1,
          },
          endPage: {
            title: "Agradecemos sua participação!",
            buttonText: "Clique na tela para finalizar",
          },
        },
      };
      return data;
    case 10:
      data = {
        result: {
          title: "Photobooth",
          description:
            "Sua presença no nosso photobooth foi muito apreciada! A foto capturada já está disponível para download. Clique no botão abaixo para acessá-la e guardar como uma lembrança do evento.",
        },
        apresentation: {
          textSide: {
            background: {
              type: "color",
              url: "",
              color: "rgba(255,255,255,1)",
            },
            value: 100,
            textColor: "rgba(0,0,0,1)",
            h5: 1.25,
            p: 1,
          },
          register: {
            title: "Preencha os campos abaixo para se inscrever",
          },
          configs: {
            buttonPosition: 20,
            buttonPositionEnd: 20,
            logoSize: 380,
            media: "image",
            videoDuration: 5,
            qrcodeEndPage: 30000,
            showLastUser: true,
            testImageUpload: false,
            printPhoto: false,
            printPhotoTray: false,
            enableFrame: true,
            noFrameOnPreview: false,
            galleryTransition: 15000,
            form: false,
            falai: {
              status: false,
              model: "fal-ai/nano-banana-pro/edit",
              prompt: {
                base: "",
                steps: [],
              },
              aspect_ratio: "auto",
              output_format: "png",
              resolution: "1K",
              num_images: 1,
              limit_generations: false,
              enable_web_search: false,
              extraReferences: [],
              containerPosition: 50,
              referenceWidth: 275,
            },
          },
          frame: "",
          frameHorizontal: "",
          startPage: {
            show: true,
            title: "Transforme sua foto com AI",
            buttonText: "Toque para começar",
            showDemoVideo: true,
          },
          firstPage: {
            buttonText: "Clique na tela para capturar sua foto",
          },
          endPage: {
            title: "Como baixar sua foto:",
            description:
              "Utilize a câmera do seu dispositivo para escanear o Código QR abaixo, que dará acesso a sua foto para que você possa baixar e compartilhar.\n\nAgradecemos sua participação",
            buttonText: "Clique na tela para finalizar",
            showPrintedImage: false,
          },
        },
      };
      return data;
    case 11:
      data = {
        displayMode: {
          value: "user",
        },
        allowMultipleSubmissions: {
          status: true,
        },
        allowFileUpload: {
          status: true,
        },
        platform1PhotoboothSource: {
          enabled: false,
          event_token: "",
          event_id: "",
          event_name: "",
          customizacao_id: "",
          customization_name: "",
        },
        platform2PhotoActivitySource: {
          enabled: false,
          event_token: "",
          event_id: "",
          event_name: "",
          activity_ids: [],
          activity_id: "",
        },
        title: {
          status: true,
          value: "Participe da nossa galeria!",
        },
        description: {
          status: true,
          value: "As fotos são enviadas automaticamente",
        },
        endPage: {
          title: {
            status: false,
            value: "",
          },
          description: {
            status: false,
            value: "",
          },
        },
        useMemzo: {
          status: false,
          value: "",
        },
        apresentation: {
          frame: {
            status: false,
            value: "",
          },
          frameHorizontal: "",
          register: {
            title: "Preencha os campos abaixo para se inscrever",
          },
          title: {
            status: true,
            value: "Participe da nossa galeria!",
          },
          imageSide: {
            value: 0,
            position: "start",
            background: "",
          },
          textSide: {
            background: {
              type: "color",
              url: "",
              color: "rgba(255,255,255,1)",
            },
            value: 100,
            textColor: "rgba(0,0,0,1)",
            h5: 1.25,
            p: 1,
          },
          configs: {
            width: 186,
            height: 219,
            newImageWidth: 392,
            newImageHeight: 458,
            columns: 6,
            rows: 3,
            newImageTotalTime: 5000,
            transitionTime: 15000,
            logoSize: 350,
            padding: 1.5,
            showLastUser: true,
            qrcodeEndPage: 30000,
            enableFrame: false,
            form: false,
            buttonPosition: 20,
            buttonPositionEnd: 20,
            floatingAnimation: true,
          },
          showQRCode: {
            text: "Leia o QRCode para participar",
            link: "",
            status: true,
            position: {
              x: "left",
              y: "bottom",
            },
          },
          firstPage: {
            buttonText: "Clique na tela para capturar sua foto",
          },
          endPage: {
            title: "Sua foto foi registrada!",
            description: "Agradecemos sua participação.",
            buttonText: "Clique na tela para finalizar",
          },
        },
        galleryOverlay: {
          background: {
            type: "image",
            value: "",
          },
          configs: {
            galleryRows: 3,
            galleryColumns: 4,
            galleryTransitionTime: 15000,
            imageWidth: 150,
            imageHeight: 150,
            galleryTop: 0,
            galleryLeft: 0,
            galleryRight: 0,
            galleryBottom: 0,
          },
        },
      };
      return data;
    case 12:
      data = {
        allowMultipleSubmissions: {
          status: true,
        },
        displayMode: {
          value: "user",
        },
        title: {
          status: true,
          value: "Bem-vindo ao Painel de Assinaturas",
        },
        description: {
          status: true,
          value: "Continuar",
        },
        endPage: {
          title: {
            status: true,
            value: "Assinatura criada com sucesso!",
          },
          description: {
            status: false,
            value: "",
          },
        },
        apresentation: {
          imageSide: {
            value: 0,
            position: "start",
            background: "",
          },
          textSide: {
            value: 100,
            textColor: "rgba(255, 255, 255, 1)",
            h5: 1.25,
            p: 1,
            background: {
              type: "color",
              url: "",
              color: "rgba(255,255,255,1)",
            },
          },
          showQRCode: {
            status: false,
            text: "Leia o QRCode",
            link: "",
            position: {
              x: "left",
              y: "bottom",
            },
          },
          configs: {
            logoSize: 300,
          },
        },
        matterPanel: {
          width: 150,
          maxElements: 30,
          timeBetween: 2000,
          speed: 0.0005,
          amplitude: 15,
        },
        appState: {
          penColor: {
            value: "#FF0000",
          },
          penWidth: {
            value: 6,
          },
        },
      };
      return data;
    default:
      return {};
  }
};

export const schemaCustomizacao = (aplicativoID) => {
  let schema = {};

  switch (aplicativoID) {
    case 1:
      schema = Yup.object().shape({
        customizacao_id: Yup.number("Valor inválido")
          .integer()
          .required("Campo obrigatório"),
        titulo: Yup.string()
          .required("Campo obrigatório")
          .max(150, "Máximo de 150 caracteres"),
        json: Yup.object().shape({
          sound: Yup.boolean().required("Campo obrigatório"),
          showScore: Yup.boolean().required("Campo obrigatório"),
          showRanking: Yup.boolean().required("Campo obrigatório"),
          reset: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            text: Yup.string().test(
              "text",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          retry: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            text: Yup.string().test(
              "text",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          timer: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.number("Valor inválido").test(
              "value",
              "O tempo precisa ser maior que 15 segundos",
              function (value) {
                if (this.parent.status) {
                  if (value > 14) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          endText: Yup.object().shape({
            title: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            description: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            showImage: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
              width: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .required("Campo obrigatório"),
            }),
            titleWinner: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            descriptionWinner: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
          }),
          apresentation: Yup.object().shape({
            rankingPage: Yup.object().shape({
              title: Yup.object().shape({
                status: Yup.boolean().required("Campo obrigatório"),
                value: Yup.string().test(
                  "value",
                  "Campo obrigatório",
                  function (value) {
                    if (this.parent.status) {
                      if (value !== "" && value !== undefined) {
                        return true;
                      } else {
                        return false;
                      }
                    } else {
                      return true;
                    }
                  },
                ),
              }),
            }),
            configs: Yup.object().shape({
              logoSize: Yup.number("Valor inválido")
                .typeError("Apenas números")
                .required("Campo obrigatório"),
            }),
            imageSide: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O Valor precisa ser menor que 100`),
              position: Yup.string().required("Campo obrigatório"),
              background: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.value > 0) {
                    if (value !== "") {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            textSide: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O Valor precisa ser menor que 100`),
              backgroundColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isBackground(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              textColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isColor(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              h5: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O Valor precisa ser maior que 0"),
              p: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O Valor precisa ser maior que 0"),
            }),
            showQRCode: Yup.object().shape({
              text: Yup.string().max(150, "Máximo de 150 caracteres"),
              link: Yup.string(),
              status: Yup.boolean().required("Campo obrigatório"),
              position: Yup.object().shape({
                x: Yup.string().required("Campo obrigatório"),
                y: Yup.string().required("Campo obrigatório"),
              }),
            }),
          }),
          memoryGame: Yup.object().shape({
            cards: Yup.object().shape({
              value: Yup.array(
                Yup.object({
                  pair: Yup.number("Valor inválido").required(
                    "Campo obrigatório",
                  ),
                  url: Yup.string().required("Campo obrigatório"),
                }),
              ),
              front: Yup.string().required("Campo obrigatório"),
              borderRadius:
                Yup.number("Valor inválido").required("Campo obrigatório"),
              width: Yup.number("Valor inválido").required("Campo obrigatório"),
              cardsPerRow:
                Yup.number("Valor inválido").required("Campo obrigatório"),
              padding:
                Yup.number("Valor inválido").required("Campo obrigatório"),
              firstPlayerColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isColor(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              firstPlayerBackgroundColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isBackground(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              secondPlayerColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isColor(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              secondPlayerBackgroundColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isBackground(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
            }),
            allowTwoPlayers: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              description: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            endGameTimer: Yup.number("Valor inválido")
              .positive("O Valor precisa ser maior que 0")
              .min(1000, "O Valor precisa ser maior que 1000"),
            flipCardTimer: Yup.number("Valor inválido")
              .positive("O Valor precisa ser maior que 0"),
            gameText: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
          }),
        }),
      });
      break;
    case 2:
      schema = Yup.object().shape({
        customizacao_id: Yup.number("Valor inválido")
          .integer()
          .required("Campo obrigatório"),
        titulo: Yup.string()
          .required("Campo obrigatório")
          .max(150, "Máximo de 150 caracteres"),
        json: Yup.object().shape({
          sound: Yup.boolean().required("Campo obrigatório"),
          showRanking: Yup.boolean().required("Campo obrigatório"),
          showChart: Yup.boolean().required("Campo obrigatório"),
          showScore: Yup.boolean().required("Campo obrigatório"),
          allowPreviousQuestion: Yup.boolean().required("Campo obrigatório"),
          shuffleQuestions: Yup.boolean().required("Campo obrigatório"),
          shuffleQuestionsArray: Yup.string().test(
            "text",
            "Formato inválido",
            function (value) {
              if (!value) {
                return true;
              }
              const isValidFormat = /^(\d+\s*,\s*)*\d+$/.test(value);
              if (isValidFormat) {
                return true;
              } else {
                return false;
              }
            },
          ),
          correctFeedback: Yup.boolean().required("Campo obrigatório"),
          answerFeedback: Yup.boolean().required("Campo obrigatório"),
          selectQuestions: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.number("Valor inválido").test(
              "text",
              "Valor precisa ser maior que 0",
              function (value) {
                if (this.parent.status) {
                  if (value > 0) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          reset: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            text: Yup.string().test(
              "text",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          retry: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            text: Yup.string().test(
              "text",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          timer: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.number("Valor inválido").test(
              "value",
              "O tempo precisa ser maior que 15 segundos",
              function (value) {
                if (this.parent.status) {
                  if (value > 14) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
            position: Yup.string().oneOf(
              ["belowQuestion", "aboveQuestion"],
              "Valor inválido",
            ),
          }),
          endText: Yup.object().shape({
            title: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            description: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            showImage: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
              width: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .required("Campo obrigatório"),
            }),
            titleWinner: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            descriptionWinner: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
          }),
          apresentation: Yup.object().shape({
            rankingPage: Yup.object().shape({
              title: Yup.object().shape({
                status: Yup.boolean().required("Campo obrigatório"),
                value: Yup.string().test(
                  "value",
                  "Campo obrigatório",
                  function (value) {
                    if (this.parent.status) {
                      if (value !== "" && value !== undefined) {
                        return true;
                      } else {
                        return false;
                      }
                    } else {
                      return true;
                    }
                  },
                ),
              }),
            }),
            configs: Yup.object().shape({
              logoSize: Yup.number("Valor inválido")
                .typeError("Apenas números")
                .required("Campo obrigatório"),
            }),
            imageSide: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              position: Yup.string().required("Campo obrigatório"),
              background: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.value > 0) {
                    if (value !== "") {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            textSide: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              backgroundColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isBackground(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              textColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isColor(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              h5: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
              p: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
            }),
            showQRCode: Yup.object().shape({
              text: Yup.string().max(150, "Máximo de 150 caracteres"),
              link: Yup.string(),
              status: Yup.boolean().required("Campo obrigatório"),
              position: Yup.object().shape({
                x: Yup.string().required("Campo obrigatório"),
                y: Yup.string().required("Campo obrigatório"),
              }),
            }),
          }),
          result: Yup.array(),
          questions: Yup.array(
            Yup.object({
              question_id:
                Yup.number("Valor inválido").required("Campo obrigatório"),
              exercise:
                Yup.number("Valor inválido")
                  .oneOf([1, 2, 3], "Tipo de exercício inválido")
                  .required("Campo obrigatório"),
              type: Yup.number("Valor inválido").required("Campo obrigatório"),
              col: Yup.number("Valor inválido").required("Campo obrigatório"),
              url: Yup.string().test("url", "Link inválido", function (value) {
                if (this.parent.type === 3) {
                  // Regular expression pattern for YouTube URL
                  let regex =
                    /(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/gm;
                  let id = regex.exec(value);
                  if (id && id[3]) {
                    return true;
                  } else {
                    return false;
                  }
                } else if (this.parent.type === 2) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              }),
              question: Yup.string()
                .required("Campo obrigatório")
                .max(500, "Máximo de 500 caracteres"),
              value: Yup.number("Valor inválido"),
              shuffleAnswers: Yup.boolean().required("Campo obrigatório"),
              enableMultipleChoice: Yup.boolean(),
              multipleChoiceMin: Yup.number("Valor inválido"),
              multipleChoiceMax: Yup.number("Valor inválido"),
              answers: Yup.array(
                Yup.object({
                  text: Yup.string().required("Campo obrigatório"),
                  isCorrect: Yup.boolean().required("Campo obrigatório"),
                  order:
                    Yup.number("Valor inválido").required("Campo obrigatório"),
                }),
              ).when("exercise", {
                is: 3,
                then: (schema) => schema,
                otherwise: (schema) => schema
                  .min(1, "Adicione ao menos uma resposta")
                  .test(
                    "correct-answer-required",
                    "Marque ao menos uma resposta correta",
                    function (answers) {
                      if (this.parent.exercise !== 1) {
                        return true;
                      }

                      return Array.isArray(answers) && answers.some((answer) => answer?.isCorrect === true);
                    },
                  ),
              }),
              additionalAnswer: Yup.object().shape({
                status: Yup.boolean().required("Campo obrigatório"),
                text: Yup.string(),
                image: Yup.string(),
              }),
            }).test(
              "multiple-choice-limits",
              "Configure limites válidos para múltiplas respostas",
              validateQuizMultipleChoiceLimits,
            ),
          ),
        }),
      });
      break;
    case 3:
      schema = Yup.object().shape({
        customizacao_id: Yup.number("Valor inválido")
          .integer()
          .required("Campo obrigatório"),
        titulo: Yup.string()
          .required("Campo obrigatório")
          .max(100, "Máximo de 100 caracteres"),
        json: Yup.object().shape({
          deleteHistory: Yup.boolean().required("Campo obrigatório"),
          showMessageHistory: Yup.boolean().required("Campo obrigatório"),
          maxlength: Yup.number("Valor inválido")
            .integer()
            .max(1000, "Valor máximo de 1000")
            .required("Campo obrigatório"),
          minlength: Yup.number("Valor inválido")
            .integer()
            .max(100, "Valor máximo de 100")
            .required("Campo obrigatório"),
          messageLimit: Yup.number("Valor inválido")
            .integer()
            .required("Campo obrigatório"),
          userFeedback: Yup.boolean().required("Campo obrigatório"),
          autoApproval: Yup.boolean().required("Campo obrigatório"),
          title: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.string().test(
              "value",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          description: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.string().test(
              "value",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          apresentation: Yup.object().shape({
            configs: Yup.object().shape({
              logoSize: Yup.number("Valor inválido")
                .typeError("Apenas números")
                .required("Campo obrigatório"),
            }),
            showIsAnswered: Yup.boolean().required("Campo obrigatório"),
            showAuthor: Yup.boolean().required("Campo obrigatório"),
            imageSide: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              position: Yup.string().required("Campo obrigatório"),
              background: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.value > 0) {
                    if (value !== "") {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            textSide: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              backgroundColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isBackground(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              fontFamily: Yup.string().required("Campo obrigatório"),
              textColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isColor(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              messageSlider: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
              h5: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
              p: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
            }),
            showQRCode: Yup.object().shape({
              text: Yup.string().max(150, "Máximo de 150 caracteres"),
              link: Yup.string(),
              status: Yup.boolean().required("Campo obrigatório"),
              position: Yup.object().shape({
                x: Yup.string().required("Campo obrigatório"),
                y: Yup.string().required("Campo obrigatório"),
              }),
            }),
          }),
        }),
      });
      break;
    case 4:
      schema = Yup.object().shape({
        customizacao_id: Yup.number("Valor inválido")
          .integer()
          .required("Campo obrigatório"),
        titulo: Yup.string()
          .required("Campo obrigatório")
          .max(100, "Máximo de 100 caracteres"),
        json: Yup.object().shape({
          type: Yup.number("Valor inválido")
            .integer()
            .required("Campo obrigatório"),
          deleteHistory: Yup.boolean().required("Campo obrigatório"),
          showMessageHistory: Yup.boolean().required("Campo obrigatório"),
          maxlength: Yup.number("Valor inválido")
            .integer()
            .max(1000, "Valor máximo de 1000")
            .required("Campo obrigatório"),
          minlength: Yup.number("Valor inválido")
            .integer()
            .max(100, "Valor máximo de 100")
            .required("Campo obrigatório"),
          messageLimit: Yup.number("Valor inválido")
            .integer()
            .required("Campo obrigatório"),
          autoApproval: Yup.boolean().required("Campo obrigatório"),
          userFeedback: Yup.boolean().required("Campo obrigatório"),
          title: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.string().test(
              "value",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          description: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.string().test(
              "value",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          reset: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
          }),
          apresentation: Yup.object().shape({
            title: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            intervalTime: Yup.number("Valor inválido")
              .integer()
              .required("Campo obrigatório"),
            showIsAnswered: Yup.boolean().required("Campo obrigatório"),
            showAuthor: Yup.boolean().required("Campo obrigatório"),
            imageSide: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              position: Yup.string().required("Campo obrigatório"),
              background: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.value > 0) {
                    if (value !== "") {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            textSide: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              backgroundColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isBackground(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              textColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isColor(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              fontFamily: Yup.string().required("Campo obrigatório"),
              messageSlider: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
              h5: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
              p: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
            }),
            configs: Yup.object().shape({
              logoSize: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .required("Campo obrigatório"),
              mode: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .required("Campo obrigatório"),
              gallery: Yup.object().shape({
                width: Yup.number("Valor inválido")
                  .positive("O Valor precisa ser maior que 0")
                  .required("Campo obrigatório"),
                height: Yup.number("Valor inválido")
                  .positive("O Valor precisa ser maior que 0")
                  .required("Campo obrigatório"),
                newItemWidth: Yup.number("Valor inválido")
                  .positive("O Valor precisa ser maior que 0")
                  .required("Campo obrigatório"),
                newItemHeight: Yup.number("Valor inválido")
                  .positive("O Valor precisa ser maior que 0")
                  .required("Campo obrigatório"),
                columns: Yup.number("Valor inválido")
                  .min(1, `O Valor precisa ser menor que 1`)
                  .max(20, `O Valor precisa ser menor que 20`)
                  .required("Campo obrigatório"),
                rows: Yup.number("Valor inválido")
                  .min(1, `O Valor precisa ser menor que 1`)
                  .max(20, `O Valor precisa ser menor que 20`)
                  .required("Campo obrigatório"),
                newItemTotalTime: Yup.number("Valor inválido")
                  .positive("O Valor precisa ser maior que 0")
                  .min(2000, "O Valor precisa ser maior que 2000")
                  .required("Campo obrigatório"),
                transitionTime: Yup.number("Valor inválido")
                  .positive("O Valor precisa ser maior que 0")
                  .min(500, "O Valor precisa ser maior que 5000")
                  .required("Campo obrigatório"),
                padding: Yup.number("Valor inválido")
                  .positive("O Valor precisa ser maior que 0")
                  .required("Campo obrigatório"),
                backgroundColor: Yup.string()
                  .required("Campo obrigatório")
                  .test("color", "Valor inválido", function (value) {
                    if (isBackground(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                color: Yup.string()
                  .required("Campo obrigatório")
                  .test("color", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
              }),
            }),
            showQRCode: Yup.object().shape({
              text: Yup.string().max(150, "Máximo de 150 caracteres"),
              link: Yup.string(),
              status: Yup.boolean().required("Campo obrigatório"),
              position: Yup.object().shape({
                x: Yup.string().required("Campo obrigatório"),
                y: Yup.string().required("Campo obrigatório"),
              }),
            }),
          }),
        }),
      });
      break;
    case 5:
      schema = Yup.object().shape({
        customizacao_id: Yup.number("Valor inválido")
          .integer()
          .required("Campo obrigatório"),
        titulo: Yup.string()
          .required("Campo obrigatório")
          .max(100, "Máximo de 100 caracteres"),
        json: Yup.object().shape({
          deleteHistory: Yup.boolean().required("Campo obrigatório"),
          showMessageHistory: Yup.boolean().required("Campo obrigatório"),
          maxlength: Yup.number("Valor inválido")
            .integer()
            .max(1000, "Valor máximo de 1000")
            .test("maxlength", "Campo obrigatório", function (value) {
              if (!this.parent.selectWords?.status) {
                if (value !== undefined && value !== null) {
                  return true;
                } else {
                  return false;
                }
              } else {
                return true;
              }
            }),
          minlength: Yup.number("Valor inválido")
            .integer()
            .max(100, "Valor máximo de 100")
            .test("minlength", "Campo obrigatório", function (value) {
              if (!this.parent.selectWords?.status) {
                if (value !== undefined && value !== null) {
                  return true;
                } else {
                  return false;
                }
              } else {
                return true;
              }
            }),
          messageLimit: Yup.number("Valor inválido")
            .integer()
            .required("Campo obrigatório"),
          maxMessagePerLimit: Yup.number("Valor inválido")
            .integer()
            .required("Campo obrigatório"),
          autoApproval: Yup.boolean().required("Campo obrigatório"),
          userFeedback: Yup.boolean().required("Campo obrigatório"),
          reset: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            text: Yup.string().test(
              "text",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          selectWords: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            options: Yup.array()
              .of(Yup.string())
              .test(
                "options",
                "Pelo menos uma opção é obrigatória quando usar palavras pré-definidas",
                function (value) {
                  if (this.parent.status) {
                    if (value && value.length > 0) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
          }),
          fallbackWords: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            options: Yup.array()
              .of(Yup.string())
              .test(
                "options",
                "Pelo menos uma opção é obrigatória se ativar palavras de fallback",
                function (value) {
                  if (this.parent.status) {
                    if (value && value.length > 0) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
          }),
          title: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.string().test(
              "value",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          description: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.string().test(
              "value",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          apresentation: Yup.object().shape({
            configs: Yup.object().shape({
              logoSize: Yup.number("Valor inválido")
                .typeError("Apenas números")
                .required("Campo obrigatório"),
            }),
            imageSide: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              position: Yup.string().required("Campo obrigatório"),
              background: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.value > 0) {
                    if (value !== "") {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            textSide: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              background: Yup.object().shape({
                type: Yup.string().required("Campo obrigatório"),
                color: Yup.string()
                  .required("Campo obrigatório")
                  .test("color", "Valor inválido", function (value) {
                    if (isBackground(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                url: Yup.string().test(
                  "url",
                  "Campo obrigatório",
                  function (value) {
                    if (this.parent.type === "image" || this.parent.type === "video") {
                      if (value !== "" && value !== undefined) {
                        return true;
                      } else {
                        return false;
                      }
                    } else {
                      return true;
                    }
                  },
                ),
                backgroundPosition: Yup.string(),
              }),
              textColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isColor(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              fontFamily: Yup.string().required("Campo obrigatório"),
              h5: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
              p: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
              configs: Yup.object().shape({
                fontSizeMin: Yup.number("Valor inválido")
                  .required("Campo obrigatório")
                  .positive("O valor precisa ser maior que 0"),
                fontSizeMax: Yup.number("Valor inválido")
                  .required("Campo obrigatório")
                  .positive("O valor precisa ser maior que 0"),
                displayTime: Yup.number("Valor inválido").positive(
                  "O valor precisa ser maior que 0",
                ),
                fontStyle: Yup.string(),
                minFontWeight: Yup.number("Valor inválido").positive(
                  "O valor precisa ser maior que 0",
                ),
                maxFontWeight: Yup.number("Valor inválido").positive(
                  "O valor precisa ser maior que 0",
                ),
                minRotation: Yup.number("Valor inválido"),
                maxRotation: Yup.number("Valor inválido"),
                spiral: Yup.string(),
                transition: Yup.string(),
                padding: Yup.number("Valor inválido"),
                timeInterval: Yup.number("Valor inválido").positive(
                  "O valor precisa ser maior que 0",
                ),
                maxWords: Yup.number("Valor inválido").positive(
                  "O valor precisa ser maior que 0",
                ),
                animationDurationMultiplier: Yup.number("Valor inválido"),
                groupDelay: Yup.number("Valor inválido"),
                scaleDuration: Yup.number("Valor inválido").positive(
                  "O valor precisa ser maior que 0",
                ),
                scaleSize: Yup.number("Valor inválido").positive(
                  "O valor precisa ser maior que 0",
                ),
                enableTooltip: Yup.boolean(),
                useGradients: Yup.boolean(),
              }),
            }),
            showQRCode: Yup.object().shape({
              text: Yup.string().max(150, "Máximo de 150 caracteres"),
              link: Yup.string(),
              status: Yup.boolean().required("Campo obrigatório"),
              position: Yup.object().shape({
                x: Yup.string().required("Campo obrigatório"),
                y: Yup.string().required("Campo obrigatório"),
              }),
            }),
          }),
        }),
      });
      break;
    case 6:
      schema = Yup.object().shape({
        customizacao_id: Yup.number("Valor inválido")
          .integer()
          .required("Campo obrigatório"),
        titulo: Yup.string()
          .required("Campo obrigatório")
          .max(100, "Máximo de 100 caracteres"),
        json: Yup.object().shape({
          title: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.string().test(
              "value",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          description: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.string().test(
              "value",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          reset: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
          }),
          prizeDrawType: Yup.number("Valor inválido")
            .integer()
            .required("Campo obrigatório"),
          drawMode: Yup.number("Valor inválido")
            .integer()
            .required("Campo obrigatório"),
          maxDraws: Yup.number("Valor inválido")
            .integer()
            .required("Campo obrigatório"),
          showResult: Yup.object().shape({
            imageWidth: Yup.number("Valor inválido")
              .typeError("Apenas números")
              .required("Campo obrigatório"),
            drawImageTimeout: Yup.number("Valor inválido")
              .positive("O Valor precisa ser maior que 0")
              .test("drawImageTimeout", "Campo obrigatório", function (value) {
                const drawMode = this.options.from[1].value.drawMode;
                if (drawMode == 2) {
                  return value !== undefined && value !== null;
                }
                return true;
              }),
            drawImage: Yup.string().test(
              "drawImage",
              "Campo obrigatório",
              function (value) {
                const drawMode = this.options.from[1].value.drawMode;
                if (drawMode == 2) {
                  return value !== undefined && value !== null;
                }
                return true;
              },
            ),
            winnerImage: Yup.string().required("Campo obrigatório"),
            winnerText: Yup.string().required("Campo obrigatório"),
            loserImage: Yup.string().required("Campo obrigatório"),
            loserText: Yup.string().required("Campo obrigatório"),
            waitingImage: Yup.string().test(
              "waitingText",
              "Campo obrigatório",
              function (value) {
                const drawMode = this.options.from[1].value.drawMode;
                if (drawMode == 1) {
                  return value !== undefined && value !== null;
                }
                return true;
              },
            ),
            waitingText: Yup.string().test(
              "waitingText",
              "Campo obrigatório",
              function (value) {
                const drawMode = this.options.from[1].value.drawMode;
                if (drawMode == 1) {
                  return value !== undefined && value !== null;
                }
                return true;
              },
            ),
          }),
          prizes: Yup.array().when("json.drawMode", {
            is: 2,
            then: Yup.array().test(
              "total-possibility",
              "A soma das possibilidades não podem ser maior que 100",
              (prizes) => {
                const totalPossibility = prizes.reduce(
                  (acc, curr) => acc + curr.possibility,
                  0,
                );
                if (totalPossibility > 100) {
                  return false;
                } else {
                  return true;
                }
              },
            ),
            otherwise: Yup.array(), // if drawMode is not 2, no requirement
          }),
          apresentation: Yup.object().shape({
            title: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            prize: Yup.object().shape({
              title: Yup.object().shape({
                status: Yup.boolean().required("Campo obrigatório"),
                value: Yup.string().test(
                  "value",
                  "Campo obrigatório",
                  function (value) {
                    if (this.parent.status) {
                      if (value !== "" && value !== undefined) {
                        return true;
                      } else {
                        return false;
                      }
                    } else {
                      return true;
                    }
                  },
                ),
              }),
              image: Yup.string().test(
                "drawImage",
                "Campo obrigatório",
                function (value) {
                  const drawMode = this.options.from[2].value.drawMode;
                  if (drawMode == 1) {
                    return value !== undefined && value !== null;
                  }
                  return true;
                },
              ),
              width: Yup.number("Valor inválido")
                .typeError("Apenas números")
                .required("Campo obrigatório"),
            }),
            configs: Yup.object().shape({
              logoSize: Yup.number("Valor inválido")
                .typeError("Apenas números")
                .required("Campo obrigatório"),
            }),
            imageSide: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              position: Yup.string().required("Campo obrigatório"),
              background: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.value > 0) {
                    if (value !== "") {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            textSide: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              backgroundColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isBackground(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              textColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isColor(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              h5: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
              p: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
            }),
            showQRCode: Yup.object().shape({
              text: Yup.string().max(150, "Máximo de 150 caracteres"),
              link: Yup.string(),
              status: Yup.boolean().required("Campo obrigatório"),
              position: Yup.object().shape({
                x: Yup.string().required("Campo obrigatório"),
                y: Yup.string().required("Campo obrigatório"),
              }),
            }),
          }),
        }),
      });
      break;
    case 7:
      schema = Yup.object().shape({
        customizacao_id: Yup.number("Valor inválido")
          .integer()
          .required("Campo obrigatório"),
        titulo: Yup.string()
          .required("Campo obrigatório")
          .max(150, "Máximo de 150 caracteres"),
        json: Yup.object().shape({
          rouletteType: Yup.string()
            .required("Campo obrigatório")
            .oneOf(["wheel", "iframe"], "Valor inválido"),
          unityUrl: Yup.string().test(
            "unityUrl",
            "Campo obrigatório quando Unity Iframe está selecionado",
            function (value) {
              if (this.parent.rouletteType === "iframe") {
                if (value && value !== "") {
                  return true;
                } else {
                  return false;
                }
              } else {
                return true;
              }
            },
          ),
          initialScreen: Yup.object().shape({
            title: Yup.string(),
            description: Yup.string(),
            buttonText: Yup.string(),
          }),
          endPage: Yup.object().shape({
            title: Yup.string(),
            buttonText: Yup.string().required("Campo obrigatório"),
          }),
          result: Yup.object().shape({
            title: Yup.string(),
            description: Yup.string(),
          }),
          apresentation: Yup.object().shape({
            register: Yup.object().shape({
              title: Yup.string(),
            }),
            configs: Yup.object().shape({
              sound: Yup.boolean().required("Campo obrigatório"),
              logoSize: Yup.number("Valor inválido").positive(
                "O Valor precisa ser maior que 0",
              ),
              qrcodeEndPage: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .min(10000, "O Valor precisa ser maior que 10000"),
              form: Yup.mixed(),
            }),
            textSide: Yup.object().shape({
              background: Yup.object().shape({
                type: Yup.string().required("Campo obrigatório"),
                color: Yup.string()
                  .required("Campo obrigatório")
                  .test("color", "Valor inválido", function (value) {
                    if (isBackground(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                url: Yup.string().test(
                  "url",
                  "Campo obrigatório",
                  function (value) {
                    if (this.parent.type === "image" || this.parent.type === "video") {
                      if (value !== "" && value !== undefined) {
                        return true;
                      } else {
                        return false;
                      }
                    } else {
                      return true;
                    }
                  },
                ),
                backgroundPosition: Yup.string(),
              }),
              textColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isColor(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              h5: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O Valor precisa ser maior que 0"),
              p: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O Valor precisa ser maior que 0"),
            }),
            endPage: Yup.object().shape({
              title: Yup.string(),
              buttonText: Yup.string().required("Campo obrigatório"),
            }),
          }),
        }),
      });
      break;
    case 10:
      schema = Yup.object().shape({
        customizacao_id: Yup.number("Valor inválido")
          .integer()
          .required("Campo obrigatório"),
        titulo: Yup.string()
          .required("Campo obrigatório")
          .max(150, "Máximo de 150 caracteres"),
        json: Yup.object().shape({
          result: Yup.object().shape({
            title: Yup.string(),
            description: Yup.string(),
          }),
          apresentation: Yup.object().shape({
            register: Yup.object().shape({
              title: Yup.string(),
            }),
            configs: Yup.object().shape({
              media: Yup.string(),
              buttonPosition: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .max(100, `O Valor precisa ser menor que 100`),
              buttonPositionEnd: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .max(100, `O Valor precisa ser menor que 100`),
              logoSize: Yup.number("Valor inválido").positive(
                "O Valor precisa ser maior que 0",
              ),
              videoDuration: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .max(10, "O Valor precisa ser menor ou igual a 10"),
              qrcodeEndPage: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .min(10000, "O Valor precisa ser maior que 10000"),
              printPhoto: Yup.boolean(),
              printPhotoTray: Yup.boolean(),
              enableFrame: Yup.boolean(),
              noFrameOnPreview: Yup.boolean(),
              showLastUser: Yup.boolean(),
              testImageUpload: Yup.boolean(),
              galleryTransition: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .min(2000, "O Valor precisa ser maior que 2000"),
              falai: Yup.object().shape({
                status: Yup.boolean(),
                model: Yup.string().when("status", {
                  is: true,
                  then: Yup.string().required("Campo obrigatório"),
                  otherwise: Yup.string(),
                }),
                prompt: Yup.object().shape({
                  base: Yup.string().test(
                    "base-required-if-falai",
                    "Campo obrigatório",
                    function (value) {
                      const falai = this.from?.[1]?.value;
                      if (falai?.status === true) {
                        if (!value || value.trim() === "") return false;
                        if (value.length > 2000)
                          return this.createError({ message: "Máximo de 2000 caracteres" });
                      }
                      return true;
                    }
                  ).test(
                    "falai-placeholders-must-match-step-format",
                    "Use apenas placeholders no formato {variavel} e {variavel.campo1}",
                    function (value) {
                      if (!value || value.trim() === "") {
                        return true;
                      }

                      const invalidTokens = [];
                      const textNestedTokens = [];
                      const steps = Array.isArray(this.parent?.steps)
                        ? this.parent.steps
                        : [];
                      const textStepIds = new Set(
                        steps
                          .filter(
                            (step) =>
                              step?.type === "text" &&
                              typeof step?.id === "string" &&
                              step.id.trim() !== "",
                          )
                          .map((step) => step.id.trim()),
                      );

                      value.replace(/\[[^[\]]+\]/g, (match) => {
                        invalidTokens.push(match);
                        return match;
                      });

                      value.replace(/\{([^}]+)\}/g, (match, rawToken) => {
                        const token = rawToken.trim();

                        if (!token || token.includes("[") || token.includes("]")) {
                          invalidTokens.push(match);
                          return match;
                        }

                        if (/^([^.{}\s]+)\.([^.{}\s]+)$/.test(token)) {
                          const [stepId] = token.split(".");

                          if (textStepIds.has(stepId)) {
                            textNestedTokens.push(match);
                          }

                          return match;
                        }

                        if (/^[^.{}\s]+$/.test(token)) {
                          return match;
                        }

                        invalidTokens.push(match);
                        return match;
                      });

                      if (invalidTokens.length > 0) {
                        return this.createError({
                          message: `Use apenas placeholders no formato {variavel} e {variavel.campo1}: ${invalidTokens.join(", ")}`,
                        });
                      }

                      if (textNestedTokens.length > 0) {
                        return this.createError({
                          message: `Etapas de texto não usam tokens compostos. Remova ou altere: ${textNestedTokens.join(", ")}`,
                        });
                      }

                      return true;
                    },
                  ),
                  steps: Yup.array().of(
                    Yup.object().shape({
                      id: Yup.string().required("Campo obrigatório"),
                      question: Yup.string().when("type", {
                        is: (type) => type !== "hidden",
                        then: Yup.string().required("Campo obrigatório"),
                        otherwise: Yup.string(),
                      }),
                      type: Yup.string()
                        .oneOf(["select", "text", "hidden"], "Tipo inválido")
                        .required("Campo obrigatório"),
                      required: Yup.boolean(),
                      autoAdvance: Yup.boolean(),
                      optionImageWidth: Yup.string(),
                      optionImageHeight: Yup.string(),
                      placeholder: Yup.string(),
                      maxLength: Yup.number("Valor inválido").when("type", {
                        is: "text",
                        then: Yup.number("Valor inválido")
                          .required("Campo obrigatório")
                          .integer("Valor inválido")
                          .min(1, "O valor precisa ser maior que 0")
                          .max(500, "O valor precisa ser menor ou igual a 500"),
                        otherwise: Yup.number("Valor inválido").notRequired(),
                      }),
                      skipIf: Yup.lazy((value) => {
                        const isEmptySkipIf =
                          value === undefined ||
                          value === null ||
                          (typeof value === "object" &&
                            !Array.isArray(value) &&
                            Object.values(value).every((item) => {
                              if (typeof item === "string") {
                                return item.trim() === "";
                              }

                              return item === undefined || item === null;
                            }));

                        if (isEmptySkipIf) {
                          return Yup.mixed().nullable().notRequired();
                        }

                        return Yup.object().shape({
                          stepId: Yup.string().required("Campo obrigatório"),
                          value: Yup.string().required("Campo obrigatório"),
                        });
                      }),
                      options: Yup.array().when("type", {
                        is: (type) => !type || type === "select" || type === "hidden",
                        then: Yup.array().of(
                          Yup.object().shape({
                            id: Yup.string().required("Campo obrigatório"),
                            label: Yup.string().test(
                              "falai-option-label-required",
                              "Campo obrigatório",
                              function (value) {
                                const stepType = this.from?.[1]?.value?.type;

                                if (stepType === "hidden") {
                                  return true;
                                }

                                return typeof value === "string" && value.trim() !== "";
                              },
                            ),
                            value: Yup.string().required("Campo obrigatório"),
                            image: Yup.string(),
                            useAsReferenceImage: Yup.boolean(),
                          }),
                        )
                          .min(1, "Adicione pelo menos uma opção")
                          .test(
                            "falai-options-unique-ids",
                            "Cada opção precisa ter um ID único na etapa",
                            function (value) {
                              const optionIds = (value || [])
                                .map((item) =>
                                  typeof item?.id === "string"
                                    ? item.id.trim()
                                    : "",
                                )
                                .filter(Boolean);

                              return new Set(optionIds).size === optionIds.length;
                            },
                          ),
                        otherwise: Yup.array(),
                      }),
                      moreOptions: Yup.array().when(["type", "options"], {
                        is: (type, options) =>
                          (type === "select" || type === "hidden") &&
                          Array.isArray(options) &&
                          options.length > 0,
                        then: Yup.array()
                          .of(
                            Yup.object().shape({
                              label: Yup.string().required("Campo obrigatório"),
                              possibilities: Yup.array()
                                .of(
                                  Yup.object().shape({
                                    id: Yup.string().required("Campo obrigatório"),
                                    values: Yup.array()
                                      .of(
                                        Yup.mixed().test(
                                          "falai-more-option-possibility-required",
                                          "Campo obrigatório",
                                          function (value) {
                                            if (typeof value === "number") {
                                              return true;
                                            }
                                            if (typeof value === "string") {
                                              return value.trim() !== "";
                                            }
                                            return value !== undefined && value !== null;
                                          },
                                        ),
                                      )
                                      .min(1, "Adicione pelo menos uma possibilidade"),
                                  }),
                                )
                                .min(1, "Adicione pelo menos uma possibilidade")
                                .test(
                                  "falai-more-option-ids-match-step-options",
                                  "Cada elemento adicional precisa ter uma entrada para cada opção da etapa",
                                  function (value) {
                                    const stepOptions = Array.isArray(
                                      this.from?.[1]?.value?.options,
                                    )
                                      ? this.from[1].value.options
                                      : [];
                                    const optionIds = stepOptions
                                      .map((item) =>
                                        typeof item?.id === "string"
                                          ? item.id.trim()
                                          : "",
                                      )
                                      .filter(Boolean);
                                    const possibilityIds = (value || [])
                                      .map((item) =>
                                        typeof item?.id === "string"
                                          ? item.id.trim()
                                          : "",
                                      )
                                      .filter(Boolean);

                                    if (new Set(possibilityIds).size !== possibilityIds.length) {
                                      return this.createError({
                                        message:
                                          "Os IDs das possibilidades precisam ser únicos em cada elemento adicional",
                                      });
                                    }

                                    if (optionIds.length !== possibilityIds.length) {
                                      return false;
                                    }

                                    return optionIds.every((id) =>
                                      possibilityIds.includes(id),
                                    );
                                  },
                                ),
                            }),
                          )
                          .test(
                            "falai-more-options-unique-labels",
                            "Os labels dos elementos adicionais precisam ser únicos na etapa",
                            function (value) {
                              const labels = (value || [])
                                .map((item) =>
                                  typeof item?.label === "string"
                                    ? item.label.trim()
                                    : "",
                                )
                                .filter(Boolean);

                              return new Set(labels).size === labels.length;
                            },
                          ),
                        otherwise: Yup.array(),
                      }),
                    }),
                  ),
                }),
                aspect_ratio: Yup.string(),
                output_format: Yup.string(),
                // resolution - Only valid for Nano Banana Pro (fal-ai/nano-banana-pro/edit)
                resolution: Yup.string().test(
                  "resolution-model-check",
                  "Resolução só é válida para o modelo Nano Banana Pro",
                  function (value) {
                    const model = this.parent.model;
                    // If model is nano-banana (not pro), resolution should not be set or will be ignored
                    if (model === "fal-ai/nano-banana/edit" && value) {
                      return true; // Allow but will be ignored by API
                    }
                    return true;
                  },
                ),
                // Supported by both Nano Banana edit models.
                limit_generations: Yup.boolean(),
                num_images: Yup.number("Valor inválido")
                  .positive("O Valor precisa ser maior que 0")
                  .max(4, "O Valor precisa ser menor que 4"),
                enable_web_search: Yup.boolean(),
                extraReferences: Yup.array().of(
                  Yup.object().shape({
                    image: Yup.string(),
                  }),
                ),
                containerPosition: Yup.number("Valor inválido")
                  .min(0, "O valor precisa ser maior ou igual a 0")
                  .max(100, "O valor precisa ser menor ou igual a 100"),
                referenceWidth: Yup.number("Valor inválido").positive(
                  "O Valor precisa ser maior que 0",
                ),
              }),
            }),
            textSide: Yup.object().shape({
              background: Yup.object().shape({
                type: Yup.string().required("Campo obrigatório"),
                color: Yup.string()
                  .required("Campo obrigatório")
                  .test("color", "Valor inválido", function (value) {
                    if (isBackground(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                url: Yup.string().test(
                  "url",
                  "Campo obrigatório",
                  function (value) {
                    if (this.parent.type === "image" || this.parent.type === "video") {
                      if (value !== "" && value !== undefined) {
                        return true;
                      } else {
                        return false;
                      }
                    } else {
                      return true;
                    }
                  },
                ),
              }),
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              textColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isColor(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              h5: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O Valor precisa ser maior que 0"),
              p: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O Valor precisa ser maior que 0"),
            }),
            frameHorizontal: Yup.string()
              .trim()
              .test(
                "conditional-required",
                "Campo obrigatório",
                function (value) {
                  const parent = this.parent; // Access the parent object
                  const media = parent?.configs?.media; // Access the nested `media` field
                  const enableFrame = parent?.configs?.enableFrame; // Access the nested `enableFrame` field
                  if (
                    enableFrame &&
                    (media === "image" ||
                      media === "image-falai") &&
                    (!value || value.trim() === "")
                  ) {
                    return false; // Fail validation if enableFrame is true, media is 'image' and frame is empty
                  }
                  return true; // Pass validation for all other cases
                },
              ),
            frame: Yup.string()
              .trim()
              .test(
                "conditional-required",
                "Campo obrigatório",
                function (value) {
                  const parent = this.parent; // Access the parent object
                  const media = parent?.configs?.media; // Access the nested `media` field
                  const enableFrame = parent?.configs?.enableFrame; // Access the nested `enableFrame` field
                  if (
                    enableFrame &&
                    (media === "image" ||
                      media === "image-falai") &&
                    (!value || value.trim() === "")
                  ) {
                    return false; // Fail validation if enableFrame is true, media is 'image' and frame is empty
                  }
                  return true; // Pass validation for all other cases
                },
              ),
            startPage: Yup.object().shape({
              show: Yup.boolean(),
              title: Yup.string(),
              buttonText: Yup.string(),
              showDemoVideo: Yup.boolean(),
            }),
            firstPage: Yup.object().shape({
              buttonText: Yup.string().required("Campo obrigatório"),
            }),
            endPage: Yup.object().shape({
              title: Yup.string(),
              description: Yup.string(),
              buttonText: Yup.string().required("Campo obrigatório"),
              showPrintedImage: Yup.boolean(),
            }),
          }),
        }),
      });
      return schema;
    case 11:
      schema = Yup.object().shape({
        customizacao_id: Yup.number("Valor inválido")
          .integer()
          .required("Campo obrigatório"),
        titulo: Yup.string()
          .required("Campo obrigatório")
          .max(100, "Máximo de 100 caracteres"),
        json: Yup.object().shape({
          displayMode: Yup.object().shape({
            value: Yup.string()
              .required("Campo obrigatório")
              .oneOf(["user", "totem"], "Valor inválido"),
          }),
          allowMultipleSubmissions: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
          }),
          allowFileUpload: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
          }),
          platform2PhotoActivitySource: Yup.object().shape({
            enabled: Yup.boolean().required("Campo obrigatório"),
            event_token: Yup.string()
              .trim()
              .matches(/^[A-Za-z0-9]{6}$/, "Informe um token válido de 6 caracteres")
              .when("enabled", {
                is: true,
                then: (schema) => schema.required("Campo obrigatório"),
                otherwise: (schema) => schema.notRequired(),
              }),
            event_id: Yup.mixed().notRequired(),
            event_name: Yup.string().notRequired(),
            activity_ids: Yup.array()
              .of(Yup.number().integer("Valor inválido").positive("Valor inválido"))
              .notRequired(),
            activity_id: Yup.number()
              .transform((value, originalValue) => (originalValue === "" ? undefined : value))
              .integer("Valor inválido")
              .positive("Valor inválido")
              .when("enabled", {
                is: true,
                then: (schema) => schema.required("Campo obrigatório"),
                otherwise: (schema) => schema.notRequired(),
              }),
          }).default(undefined).notRequired(),
          platform1PhotoboothSource: Yup.object().shape({
            enabled: Yup.boolean().required("Campo obrigat\u00f3rio"),
            event_token: Yup.string()
              .trim()
              .matches(/^[A-Za-z0-9]{6}$/, "Informe um token v\u00e1lido de 6 caracteres")
              .when("enabled", {
                is: true,
                then: (schema) => schema.required("Campo obrigat\u00f3rio"),
                otherwise: (schema) => schema.notRequired(),
              }),
            event_id: Yup.mixed().notRequired(),
            event_name: Yup.string().notRequired(),
            customizacao_id: Yup.number()
              .transform((value, originalValue) => (originalValue === "" ? undefined : value))
              .integer("Valor inv\u00e1lido")
              .positive("Valor inv\u00e1lido")
              .when("enabled", {
                is: true,
                then: (schema) => schema.required("Campo obrigat\u00f3rio"),
                otherwise: (schema) => schema.notRequired(),
              }),
            customization_name: Yup.string().notRequired(),
          }).default(undefined).notRequired(),
          title: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.string().test(
              "value",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          description: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.string().test(
              "value",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          endPage: Yup.object().shape({
            title: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            description: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
          }),
          useMemzo: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.string().test(
              "value",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          apresentation: Yup.object().shape({
            register: Yup.object().shape({
              title: Yup.string(),
            }),
            frame: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            frameHorizontal: Yup.string(),
            title: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            imageSide: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              position: Yup.string().required("Campo obrigatório"),
              background: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.value > 0) {
                    if (value !== "") {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            textSide: Yup.object().shape({
              background: Yup.object().shape({
                type: Yup.string().required("Campo obrigatório"),
                color: Yup.string()
                  .required("Campo obrigatório")
                  .test("color", "Valor inválido", function (value) {
                    if (isBackground(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                url: Yup.string().test(
                  "url",
                  "Campo obrigatório",
                  function (value) {
                    if (this.parent.type === "image") {
                      if (value !== "" && value !== undefined) {
                        return true;
                      } else {
                        return false;
                      }
                    } else {
                      return true;
                    }
                  },
                ),
              }),
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              textColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isColor(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              h5: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
              p: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
            }),
            configs: Yup.object().shape({
              logoSize: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .required("Campo obrigatório"),
              width: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .required("Campo obrigatório"),
              height: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .required("Campo obrigatório"),
              newImageWidth: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .required("Campo obrigatório"),
              newImageHeight: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .required("Campo obrigatório"),
              columns: Yup.number("Valor inválido")
                .min(1, `O Valor precisa ser menor que 1`)
                .max(20, `O Valor precisa ser menor que 20`)
                .required("Campo obrigatório"),
              rows: Yup.number("Valor inválido")
                .min(1, `O Valor precisa ser menor que 1`)
                .max(20, `O Valor precisa ser menor que 20`)
                .required("Campo obrigatório"),
              newImageTotalTime: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .min(2000, "O Valor precisa ser maior que 2000")
                .required("Campo obrigatório"),
              transitionTime: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .min(500, "O Valor precisa ser maior que 5000")
                .required("Campo obrigatório"),
              padding: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .required("Campo obrigatório"),
              showLastUser: Yup.boolean(),
              enableFrame: Yup.boolean(),
              form: Yup.mixed(),
              qrcodeEndPage: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .min(1000, "O Valor precisa ser maior que 1000"),
              floatingAnimation: Yup.boolean(),
            }),
            endPage: Yup.object().shape({
              title: Yup.string(),
              description: Yup.string(),
              buttonText: Yup.string(),
            }),
            showQRCode: Yup.object().shape({
              text: Yup.string().max(150, "Máximo de 150 caracteres"),
              link: Yup.string(),
              status: Yup.boolean().required("Campo obrigatório"),
              position: Yup.object().shape({
                x: Yup.string().required("Campo obrigatório"),
                y: Yup.string().required("Campo obrigatório"),
              }),
            }),
          }),
          galleryOverlay: Yup.object().shape({
            background: Yup.object().shape({
              type: Yup.string().required("Campo obrigatório"),
              value: Yup.string().test(
                "url",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.type === "image" || this.parent.type === "video") {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            configs: Yup.object().shape({
              galleryRows: Yup.number("Valor inválido")
                .min(1, "O valor precisa ser maior que 1")
                .max(20, "O valor precisa ser menor que 20")
                .required("Campo obrigatório"),
              galleryColumns: Yup.number("Valor inválido")
                .min(1, "O valor precisa ser maior que 1")
                .max(20, "O valor precisa ser menor que 20")
                .required("Campo obrigatório"),
              galleryTransitionTime: Yup.number("Valor inválido")
                .min(1000, "O Valor precisa ser maior que 1000")
                .required("Campo obrigatório"),
              imageWidth: Yup.number("Valor inválido")
                .min(50, "O valor precisa ser maior que 50")
                .max(2000, "O valor precisa ser menor que 2000")
                .required("Campo obrigatório"),
              imageHeight: Yup.number("Valor inválido")
                .min(50, "O valor precisa ser maior que 50")
                .max(2000, "O valor precisa ser menor que 2000")
                .required("Campo obrigatório"),
              galleryTop: Yup.number("Valor inválido")
                .integer()
                .max(100, "O valor precisa ser menor que 100")
                .required("Campo obrigatório"),
              galleryLeft: Yup.number("Valor inválido")
                .integer()
                .max(100, "O valor precisa ser menor que 100")
                .required("Campo obrigatório"),
              galleryRight: Yup.number("Valor inválido")
                .integer()
                .max(100, "O valor precisa ser menor que 100")
                .required("Campo obrigatório"),
              galleryBottom: Yup.number("Valor inválido")
                .integer()
                .max(100, "O valor precisa ser menor que 100")
                .required("Campo obrigatório"),
            }),
          }),
        }),
      });
      break;

    case 12:
      schema = Yup.object().shape({
        customizacao_id: Yup.number("Valor inválido")
          .integer()
          .required("Campo obrigatório"),
        titulo: Yup.string()
          .required("Campo obrigatório")
          .max(100, "Máximo de 100 caracteres"),
        json: Yup.object().shape({
          allowMultipleSubmissions: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
          }),
          displayMode: Yup.object().shape({
            value: Yup.string()
              .required("Campo obrigatório")
              .oneOf(["user", "totem"], "Valor inválido"),
          }),
          title: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.string().test(
              "value",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          description: Yup.object().shape({
            status: Yup.boolean().required("Campo obrigatório"),
            value: Yup.string().test(
              "value",
              "Campo obrigatório",
              function (value) {
                if (this.parent.status) {
                  if (value !== "" && value !== undefined) {
                    return true;
                  } else {
                    return false;
                  }
                } else {
                  return true;
                }
              },
            ),
          }),
          endPage: Yup.object().shape({
            title: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            description: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.status) {
                    if (value !== "" && value !== undefined) {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
          }),
          apresentation: Yup.object().shape({
            imageSide: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              position: Yup.string().required("Campo obrigatório"),
              background: Yup.string().test(
                "value",
                "Campo obrigatório",
                function (value) {
                  if (this.parent.value > 0) {
                    if (value !== "") {
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return true;
                  }
                },
              ),
            }),
            textSide: Yup.object().shape({
              background: Yup.object().shape({
                type: Yup.string().required("Campo obrigatório"),
                color: Yup.string()
                  .required("Campo obrigatório")
                  .test("color", "Valor inválido", function (value) {
                    if (isBackground(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                url: Yup.string().test(
                  "url",
                  "Campo obrigatório",
                  function (value) {
                    if (this.parent.type === "image") {
                      if (value !== "" && value !== undefined) {
                        return true;
                      } else {
                        return false;
                      }
                    } else {
                      return true;
                    }
                  },
                ),
              }),
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .max(100, `O valor precisa ser menor que 100`),
              textColor: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isColor(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
              h5: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
              p: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0"),
            }),
            configs: Yup.object().shape({
              logoSize: Yup.number("Valor inválido")
                .positive("O Valor precisa ser maior que 0")
                .required("Campo obrigatório"),
            }),
            showQRCode: Yup.object().shape({
              text: Yup.string().max(150, "Máximo de 150 caracteres"),
              link: Yup.string(),
              status: Yup.boolean().required("Campo obrigatório"),
              position: Yup.object().shape({
                x: Yup.string().required("Campo obrigatório"),
                y: Yup.string().required("Campo obrigatório"),
              }),
            }),
          }),
          matterPanel: Yup.object().shape({
            width: Yup.number("Valor inválido")
              .required("Campo obrigatório")
              .positive("O valor precisa ser maior que 0")
              .min(50, "O valor precisa ser maior ou igual a 50")
              .max(300, "O valor precisa ser menor ou igual a 300"),
            maxElements: Yup.number("Valor inválido")
              .required("Campo obrigatório")
              .positive("O valor precisa ser maior que 0")
              .min(5, "O valor precisa ser maior ou igual a 5")
              .max(100, "O valor precisa ser menor ou igual a 100"),
            timeBetween: Yup.number("Valor inválido")
              .required("Campo obrigatório")
              .positive("O valor precisa ser maior que 0")
              .min(100, "O valor precisa ser maior ou igual a 100")
              .max(5000, "O valor precisa ser menor ou igual a 5000"),
            speed: Yup.number("Valor inválido")
              .required("Campo obrigatório")
              .positive("O valor precisa ser maior que 0")
              .min(0.0001, "O valor precisa ser maior ou igual a 0.0001")
              .max(0.002, "O valor precisa ser menor ou igual a 0.002"),
            amplitude: Yup.number("Valor inválido")
              .required("Campo obrigatório")
              .positive("O valor precisa ser maior que 0")
              .min(5, "O valor precisa ser maior ou igual a 5")
              .max(100, "O valor precisa ser menor ou igual a 100"),
          }),
          appState: Yup.object().shape({
            penColor: Yup.object().shape({
              value: Yup.string()
                .required("Campo obrigatório")
                .test("color", "Valor inválido", function (value) {
                  if (isColor(value)) {
                    return true;
                  } else {
                    return false;
                  }
                }),
            }),
            penWidth: Yup.object().shape({
              value: Yup.number("Valor inválido")
                .required("Campo obrigatório")
                .positive("O valor precisa ser maior que 0")
                .integer("O valor precisa ser um número inteiro")
                .min(1, "O valor precisa ser maior ou igual a 1")
                .max(20, "O valor precisa ser menor ou igual a 20"),
            }),
          }),
        }),
      });
      break;

    default:
      break;
  }

  return schema;
};

export const schemaEventMode01 = (
  values = {},
  initialValues = {},
  registeredParticipants = 0,
) => {
  let schema = Yup.object().shape({
    evento_id: Yup.number("Valor inválido")
      .integer()
      .required("Campo obrigatório"),
    titulo: Yup.string()
      .required("Campo obrigatório")
      .max(150, "Máximo de 150 caracteres"),
    descricao: Yup.string().max(255, "Máximo de 255 caracteres").nullable(),
    status: Yup.number("Valor inválido")
      .required("Campo obrigatório")
      .integer(),
    data_criacao: Yup.string()
      .required("Campo obrigatório")
      .max(255, "Máximo de 255 caracteres")
      .test("date", "Data inválida", function (valueX) {
        if (validateDate(valueX)) {
          return true;
        } else {
          return false;
        }
      }),
    data_termino: Yup.string()
      .required("Campo obrigatório")
      .max(255, "Máximo de 255 caracteres")
      .test("date", "Data inválida", function (valueX) {
        // Only validate if value is different from initial value
        if (valueX === initialValues.data_termino) {
          return true;
        }

        if (validateDate(valueX) && isDateAfterToday(valueX)) {
          return true;
        } else {
          return false;
        }
      })
      .test("date-after-creation", "Data inválida", function (valueX) {
        // Only validate if value is different from initial value
        if (valueX === initialValues.data_termino) {
          return true;
        }

        const { data_criacao } = this.parent;
        if (
          data_criacao &&
          valueX &&
          validateDate(data_criacao) &&
          validateDate(valueX)
        ) {
          // Parse dates using date-fns with Brazilian format
          const creationDateWithSeconds = parse(
            data_criacao,
            "dd/MM/yyyy HH:mm:ss",
            new Date(),
          );
          const creationDateWithoutSeconds = parse(
            data_criacao,
            "dd/MM/yyyy HH:mm",
            new Date(),
          );
          const creationDateObj = isValid(creationDateWithSeconds)
            ? creationDateWithSeconds
            : creationDateWithoutSeconds;

          const endDateWithSeconds = parse(
            valueX,
            "dd/MM/yyyy HH:mm:ss",
            new Date(),
          );
          const endDateWithoutSeconds = parse(
            valueX,
            "dd/MM/yyyy HH:mm",
            new Date(),
          );
          const endDateObj = isValid(endDateWithSeconds)
            ? endDateWithSeconds
            : endDateWithoutSeconds;

          if (isValid(creationDateObj) && isValid(endDateObj)) {
            return isAfter(endDateObj, creationDateObj);
          } else {
            return false;
          }
        }
        return true;
      }),
    json: Yup.object().shape({
      personalizationType: Yup.string().oneOf(["simplified", "advanced"]),
      background: Yup.object().shape({
        type: Yup.string().required("Campo obrigatório"),
        color: Yup.string()
          .required("Campo obrigatório")
          .test("color", "Valor inválido", function (value) {
            if (isBackground(value)) {
              return true;
            } else {
              return false;
            }
          }),
        url: Yup.string().test("url", "Campo obrigatório", function (value) {
          if (
            this.parent.type === "image" ||
            this.parent.type === "video"
          ) {
            if (value !== "" && value !== undefined) {
              return true;
            } else {
              return false;
            }
          } else {
            return true;
          }
        }),
      }),
      mainLayout: Yup.object().shape({
        justifyContent: Yup.string().required("Campo obrigatório"),
        paddingBox: Yup.string()
          .required("Campo obrigatório")
          .test("paddingBox", "Formato inválido", function (value) {
            if (!isValidMarginFormat(value)) {
              return false;
            } else {
              return true;
            }
          }),
      }),
      layout: Yup.object().shape({
        type: Yup.number("Valor inválido").required("Campo obrigatório"),
        footer: Yup.object().shape({
          type: Yup.string().required("Campo obrigatório"),
          url: Yup.string().when("type", {
            is: "image",
            then: Yup.string().required("Campo obrigatório"),
            otherwise: Yup.string(),
          }),
          width: Yup.string().when("type", {
            is: "image",
            then: Yup.string().required("Campo obrigatório"),
            otherwise: Yup.string(),
          }),
          socialMedia: Yup.array(),
        }),
      }),
      subscriptionLimit: Yup.object().shape({
        status: Yup.boolean().required("Campo obrigatório"),
        limit: Yup.number("Valor inválido")
          .integer()
          .positive("O valor precisa ser maior que 0")
          .nullable()
          .test("limit", "Campo obrigatório", function (value) {
            if (this.parent.status) {
              if (value !== null && value !== undefined) {
                return true;
              } else {
                return false;
              }
            } else {
              return true;
            }
          })
          .test(
            "limit-not-below-registered",
            "O limite deve ser maior ou igual ao total de participantes já registrados",
            function (value) {
              if (!this.parent.status || value === null || value === undefined) {
                return true;
              }
              return Number(value) >= Number(registeredParticipants || 0);
            },
          ),
        disableFormTitle: Yup.string(),
        disableDescription: Yup.string(),
      }),
      noCache: Yup.boolean(),
      logo: Yup.string().required("Campo obrigatório"),
      maskableIcon: Yup.string(),
      ogImage: Yup.string(),
      metaTagTitle: Yup.string().max(150, "Máximo de 150 caracteres"),
      metaTagDescription: Yup.string()
        .max(255, "Máximo de 255 caracteres")
        .nullable(),
      logoWidth: Yup.number("Valor inválido")
        .typeError("Apenas números")
        .required("Campo obrigatório"),
      fontFamily: Yup.string().required("Campo obrigatório"),
      h5Size: Yup.number("Valor inválido")
        .typeError("Apenas números")
        .required("Campo obrigatório")
        .test("is-decimal", "Apenas valores decimais são permitidos", (value) =>
          (value + "").match(/^\d+(\.\d{1,2})?$/),
        ),
      pSize: Yup.number("Valor inválido")
        .typeError("Apenas números")
        .required("Campo obrigatório")
        .test("is-decimal", "Apenas valores decimais são permitidos", (value) =>
          (value + "").match(/^\d+(\.\d{1,2})?$/),
        ),
      paddingBox: Yup.string()
        .required("Campo obrigatório")
        .test("paddingBox", "Formato inválido", function (value) {
          if (!isValidMarginFormat(value)) {
            return false;
          } else {
            return true;
          }
        }),
      transparency: Yup.string()
        .required("Campo obrigatório")
        .test("transparency", "Valor inválido", function (value) {
          if (isBackground(value)) {
            return true;
          } else {
            return false;
          }
        }),
      primaryColor: Yup.string()
        .required("Campo obrigatório")
        .test("primaryColor", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }),
      primaryColorText: Yup.string()
        .required("Campo obrigatório")
        .test("primaryColorText", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }),
      baseColorText: Yup.string()
        .required("Campo obrigatório")
        .test("baseColorText", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }),
      presenterBackground: Yup.string()
        .required("Campo obrigatório")
        .test("presenterBackground", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }),
      boxShadow: Yup.string()
        .required("Campo obrigatório")
        .test("boxShadow", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }),
      bsBorderColor: Yup.string()
        .required("Campo obrigatório")
        .test("bsBorderColor", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }),
      end: Yup.object().shape({
        title: Yup.object().shape({
          status: Yup.boolean(),
          value: Yup.string(),
        }),
        description: Yup.object().shape({
          status: Yup.boolean(),
          value: Yup.string(),
        }),
        showUserData: Yup.boolean(),
        showQRCode: Yup.boolean(),
        reset: Yup.boolean(),
      }),
      applications: Yup.object().shape({
        title: Yup.object().shape({
          status: Yup.boolean(),
          value: Yup.string(),
        }),
        description: Yup.object().shape({
          status: Yup.boolean(),
          value: Yup.string(),
        }),
        reset: Yup.boolean(),
      }),
      form: Yup.object().shape({
        title: Yup.object().shape({
          status: Yup.boolean(),
          value: Yup.string(),
        }),
        description: Yup.object().shape({
          status: Yup.boolean(),
          value: Yup.string(),
        }),
        status: Yup.boolean(),
        buttonText: Yup.string()
          .required("Campo obrigatório")
          .max(50, "Máximo de 50 caracteres"),
        validEmails: Yup.object().shape({
          status: Yup.boolean(),
          list: Yup.array().when("status", {
            is: true,
            then: Yup.array()
              .of(Yup.string())
              .min(1, "Adicione pelo menos um domínio permitido"),
            otherwise: Yup.array(),
          }),
        }),
        authorizationMessage: Yup.mixed()
          .nullable()
          .when([], {
            is: (value) => typeof value === "object" && value !== null,
            then: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().when("status", {
                is: true,
                then: Yup.string().trim().required("Campo obrigatório"),
                otherwise: Yup.string(),
              }),
            }),
            otherwise: Yup.mixed().notRequired(),
          }),
        register_start_date: Yup.string()
          .required("Campo obrigatório")
          .max(255, "Máximo de 255 caracteres")
          .test("date", "Data inválida", function (valueX) {
            // Only validate if value is different from initial value
            if (valueX === initialValues?.json?.form?.register_start_date) {
              return true;
            }

            if (validateDate(valueX)) {
              return true;
            } else {
              return false;
            }
          })
          .test(
            "date-between-event-dates",
            "Data deve estar entre a data de criação e data de término do evento",
            function (valueX) {
              // Only validate if value is different from initial value
              if (valueX === initialValues?.json?.form?.register_start_date) {
                return true;
              }

              const { data_criacao, data_termino } = values;

              if (
                data_criacao &&
                data_termino &&
                valueX &&
                validateDate(data_criacao) &&
                validateDate(data_termino) &&
                validateDate(valueX)
              ) {
                // Parse dates using dd/MM/yyyy HH:mm format
                const creationDateObj = parse(
                  data_criacao,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );
                const endDateObj = parse(
                  data_termino,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );
                const valueDateObj = parse(
                  valueX,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );

                if (
                  isValid(creationDateObj) &&
                  isValid(endDateObj) &&
                  isValid(valueDateObj)
                ) {
                  return (
                    (isAfter(valueDateObj, creationDateObj) ||
                      isEqual(valueDateObj, creationDateObj)) &&
                    (isAfter(endDateObj, valueDateObj) ||
                      isEqual(endDateObj, valueDateObj))
                  );
                } else {
                  return false;
                }
              }

              return true;
            },
          ),
        register_end_date: Yup.string()
          .required("Campo obrigatório")
          .max(255, "Máximo de 255 caracteres")
          .test("date", "Data inválida", function (valueX) {
            // Only validate if value is different from initial value
            if (valueX === initialValues?.json?.form?.register_end_date) {
              return true;
            }

            if (validateDate(valueX)) {
              return true;
            } else {
              return false;
            }
          })
          .test("date-after-start", "Data inválida", function (valueX) {
            // Only validate if value is different from initial value
            if (valueX === initialValues?.json?.form?.register_end_date) {
              return true;
            }

            const { register_start_date } = this.parent;
            if (
              register_start_date &&
              valueX &&
              validateDate(register_start_date) &&
              validateDate(valueX)
            ) {
              // Parse dates using dd/MM/yyyy HH:mm format
              const startDateObj = parse(
                register_start_date,
                "dd/MM/yyyy HH:mm",
                new Date(),
              );
              const endDateObj = parse(valueX, "dd/MM/yyyy HH:mm", new Date());

              if (isValid(startDateObj) && isValid(endDateObj)) {
                return isAfter(endDateObj, startDateObj);
              } else {
                return false;
              }
            }
            return true;
          })
          .test(
            "date-between-event-dates",
            "Data deve estar entre a data de criação e data de término do evento",
            function (valueX) {
              // Only validate if value is different from initial value
              if (valueX === initialValues?.json?.form?.register_end_date) {
                return true;
              }

              const { data_criacao, data_termino } = values;

              if (
                data_criacao &&
                data_termino &&
                valueX &&
                validateDate(data_criacao) &&
                validateDate(data_termino) &&
                validateDate(valueX)
              ) {
                // Parse dates using dd/MM/yyyy HH:mm format
                const creationDateObj = parse(
                  data_criacao,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );
                const endDateObj = parse(
                  data_termino,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );
                const valueDateObj = parse(
                  valueX,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );

                if (
                  isValid(creationDateObj) &&
                  isValid(endDateObj) &&
                  isValid(valueDateObj)
                ) {
                  return (
                    (isAfter(valueDateObj, creationDateObj) ||
                      isEqual(valueDateObj, creationDateObj)) &&
                    (isAfter(endDateObj, valueDateObj) ||
                      isEqual(endDateObj, valueDateObj))
                  );
                } else {
                  return false;
                }
              }

              return true;
            },
          ),
        authorizationLink: Yup.string().url("URL inválida"),
        disableFormTitle: Yup.string(),
        disableDescription: Yup.string(),
        fields: Yup.array(),
        fieldsUsers: Yup.array(),
        fieldsToShow: Yup.array(),
      }),
    }),
  });
  return schema;
};

export const schemaEventMode02 = (
  values = {},
  initialValues = {},
  registeredParticipants = 0,
) => {
  const networkingStatus = values?.json?.networking?.status;

  let schema = Yup.object().shape({
    event_id: Yup.number("Valor inválido")
      .integer()
      .required("Campo obrigatório"),
    title: Yup.string()
      .required("Campo obrigatório")
      .max(150, "Máximo de 150 caracteres"),
    description: Yup.string().max(255, "Máximo de 255 caracteres").nullable(),
    status: Yup.number("Valor inválido")
      .required("Campo obrigatório")
      .integer(),
    creation_date: Yup.string()
      .required("Campo obrigatório")
      .max(255, "Máximo de 255 caracteres")
      .test("date", "Data inválida", function (valueX) {
        if (validateDate(valueX)) {
          return true;
        } else {
          return false;
        }
      }),
    end_date: Yup.string()
      .required("Campo obrigatório")
      .max(255, "Máximo de 255 caracteres")
      .test("date", "Data inválida", function (valueX) {
        // Only validate if value is different from initial value
        if (valueX === initialValues.end_date) {
          return true;
        }

        if (validateDate(valueX) && isDateAfterToday(valueX)) {
          return true;
        } else {
          return false;
        }
      })
      .test("date-after-creation", "Data inválida", function (valueX) {
        // Only validate if value is different from initial value
        if (valueX === initialValues.end_date) {
          return true;
        }

        const { creation_date } = this.parent;
        if (
          creation_date &&
          valueX &&
          validateDate(creation_date) &&
          validateDate(valueX)
        ) {
          // Parse dates using date-fns with Brazilian format
          const creationDateWithSeconds = parse(
            creation_date,
            "dd/MM/yyyy HH:mm:ss",
            new Date(),
          );
          const creationDateWithoutSeconds = parse(
            creation_date,
            "dd/MM/yyyy HH:mm",
            new Date(),
          );
          const creationDateObj = isValid(creationDateWithSeconds)
            ? creationDateWithSeconds
            : creationDateWithoutSeconds;

          const endDateWithSeconds = parse(
            valueX,
            "dd/MM/yyyy HH:mm:ss",
            new Date(),
          );
          const endDateWithoutSeconds = parse(
            valueX,
            "dd/MM/yyyy HH:mm",
            new Date(),
          );
          const endDateObj = isValid(endDateWithSeconds)
            ? endDateWithSeconds
            : endDateWithoutSeconds;

          if (isValid(creationDateObj) && isValid(endDateObj)) {
            return isAfter(endDateObj, creationDateObj);
          } else {
            return false;
          }
        }
        return true;
      }),
    json: Yup.object().shape({
      email: Yup.object().shape({
        register: Yup.object().shape({
          status: Yup.boolean().required("Campo obrigatório"),
          template_id: Yup.number().nullable().notRequired(),
          template_version: Yup.number().nullable().notRequired(),
          templates: Yup.object().shape({
            "0": Yup.object().shape({
              template_id: Yup.number().nullable().notRequired(),
              template_version: Yup.number().nullable().notRequired(),
            }).nullable().notRequired(),
            "1": Yup.object().shape({
              template_id: Yup.number().nullable().notRequired(),
              template_version: Yup.number().nullable().notRequired(),
            }).nullable().notRequired(),
            "2": Yup.object().shape({
              template_id: Yup.number().nullable().notRequired(),
              template_version: Yup.number().nullable().notRequired(),
            }).nullable().notRequired(),
          }).nullable().notRequired(),
        }),
      }),
      credentialAccess: Yup.object().shape({
        status: Yup.boolean(),
        loginCode: Yup.string().test(
          "loginCode",
          "Campo obrigatório",
          function (value) {
            // Access the current form values to check credentialAccess status
            const credentialAccessStatus =
              values?.json?.credentialAccess?.status;
            if (credentialAccessStatus) {
              if (value !== "" && value !== undefined) {
                return true;
              } else {
                return false;
              }
            } else {
              return true;
            }
          },
        ),
        monitorCode: Yup.string()
          .test("monitorCodeFormat", "Informe um código numérico de 6 dígitos", function (value) {
            const credentialAccessStatus = values?.json?.credentialAccess?.status;
            if (!credentialAccessStatus) return true;
            return /^\d{6}$/.test(String(value ?? ""));
          })
          .test("monitorCodeDistinct", "O código do monitor deve ser diferente do código do admin", function (value) {
            const credentialAccessStatus = values?.json?.credentialAccess?.status;
            if (!credentialAccessStatus || value === undefined || value === "") return true;
            return String(value) !== String(values?.codigo_credenciamento ?? initialValues?.codigo_credenciamento ?? "");
          }),
        checkinEdit: Yup.boolean(),
        newRegister: Yup.boolean(),
        removeEmail: Yup.boolean(),
        noCache: Yup.boolean(),
        zplModels: Yup.array(),
        apresentation: Yup.lazy(() => {
          // Check if credentialAccess status is true
          const credentialAccessStatus = values?.json?.credentialAccess?.status;

          if (credentialAccessStatus) {
            // Return full validation schema when credentialAccess is enabled
            return Yup.object().shape({
              title: Yup.object().shape({
                status: Yup.boolean(),
                value: Yup.string(),
              }),
              description: Yup.object().shape({
                status: Yup.boolean(),
                value: Yup.string(),
              }),
              logo: Yup.string().required("Campo obrigatório"),
              logoNav: Yup.string(),
              maskableIcon: Yup.string(),
              ogImage: Yup.string(),
              metaTagTitle: Yup.string().max(150, "Máximo de 150 caracteres"),
              metaTagDescription: Yup.string()
                .max(255, "Máximo de 255 caracteres")
                .nullable(),
              logoWidth: Yup.number("Valor inválido")
                .typeError("Apenas números"),
              imageSide: Yup.object().shape({
                value: Yup.number("Valor inválido")
                  .required("Campo obrigatório")
                  .min(0, "O valor não pode ser negativo")
                  .max(100, `O valor precisa ser menor que 100`),
                position: Yup.string().required("Campo obrigatório"),
                background: Yup.string().test(
                  "value",
                  "Campo obrigatório",
                  function (value) {
                    if (this.parent.value > 0) {
                      if (value !== "" && value !== undefined) {
                        return true;
                      } else {
                        return false;
                      }
                    } else {
                      return true;
                    }
                  },
                ),
              }),
              textSide: Yup.object().shape({
                background: Yup.object().shape({
                  type: Yup.string().required("Campo obrigatório"),
                  color: Yup.string()
                    .required("Campo obrigatório")
                    .test("color", "Valor inválido", function (value) {
                      if (isBackground(value)) {
                        return true;
                      } else {
                        return false;
                      }
                    }),
                  url: Yup.string().test(
                    "url",
                    "Campo obrigatório",
                    function (value) {
                      if (this.parent.type === "image") {
                        if (value !== "" && value !== undefined) {
                          return true;
                        } else {
                          return false;
                        }
                      } else {
                        return true;
                      }
                    },
                  ),
                }),
                value: Yup.number("Valor inválido")
                  .required("Campo obrigatório")
                  .min(0, "O valor não pode ser negativo")
                  .max(100, `O valor precisa ser menor que 100`),
                fontFamily: Yup.string().required("Campo obrigatório"),
                primaryColor: Yup.string()
                  .required("Campo obrigatório")
                  .test("primaryColor", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                primaryColorText: Yup.string()
                  .required("Campo obrigatório")
                  .test("primaryColorText", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                bodyColorText: Yup.string()
                  .required("Campo obrigatório")
                  .test("bodyColorText", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                borderColor: Yup.string()
                  .required("Campo obrigatório")
                  .test("borderColor", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                bodyBackgroundColor: Yup.string()
                  .required("Campo obrigatório")
                  .test(
                    "bodyBackgroundColor",
                    "Valor inválido",
                    function (value) {
                      if (isBackground(value)) {
                        return true;
                      } else {
                        return false;
                      }
                    },
                  ),
                containerColor: Yup.string()
                  .required("Campo obrigatório")
                  .test("containerColor", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                secondaryColor: Yup.string()
                  .required("Campo obrigatório")
                  .test("secondaryColor", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                secondaryColorText: Yup.string()
                  .required("Campo obrigatório")
                  .test("secondaryColorText", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                navBackgroundColor: Yup.string()
                  .required("Campo obrigatório")
                  .test("navBackgroundColor", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                navPrimaryColor: Yup.string()
                  .required("Campo obrigatório")
                  .test("navPrimaryColor", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                modalBackgroundColor: Yup.string()
                  .required("Campo obrigatório")
                  .test("modalBackgroundColor", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                modalColorText: Yup.string()
                  .required("Campo obrigatório")
                  .test("modalColorText", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                h5: Yup.number("Valor inválido")
                  .required("Campo obrigatório")
                  .positive("O valor precisa ser maior que 0"),
                p: Yup.number("Valor inválido")
                  .required("Campo obrigatório")
                  .positive("O valor precisa ser maior que 0"),
              }),
              checkin: Yup.object().shape({
                textSelect: Yup.string().required("Campo obrigatório"),
                textScan: Yup.string().test(
                  "textScan",
                  "Campo obrigatório",
                  function (value) {
                    const checkinType =
                      values?.json?.credentialAccess?.apresentation?.checkin
                        ?.type;
                    if (checkinType !== 1) {
                      if (value !== "" && value !== undefined) {
                        return true;
                      } else {
                        return false;
                      }
                    } else {
                      return true;
                    }
                  },
                ),
                type: Yup.number("Valor inválido").required(
                  "Campo obrigatório",
                ),
                initialType:
                  Yup.number("Valor inválido").required("Campo obrigatório"),
                fieldsToSearch: Yup.array().min(
                  1,
                  "Selecione pelo menos um campo para busca",
                ),
                fieldsForLabel: Yup.array(),
              }),
              registrationUpdate: Yup.object().shape({
                checkin: Yup.object().shape({
                  textSelect: Yup.string().required("Campo obrigatório"),
                  textScan: Yup.string().test(
                    "registrationUpdateTextScan",
                    "Campo obrigatório",
                    function (value) {
                      const checkinType =
                        values?.json?.credentialAccess?.apresentation
                          ?.registrationUpdate?.checkin?.type;
                      if (checkinType !== 1) {
                        if (value !== "" && value !== undefined) {
                          return true;
                        } else {
                          return false;
                        }
                      } else {
                        return true;
                      }
                    },
                  ),
                  type: Yup.number("Valor inválido").required(
                    "Campo obrigatório",
                  ),
                  initialType: Yup.number("Valor inválido").required(
                    "Campo obrigatório",
                  ),
                  fieldsToSearch: Yup.array().min(
                    1,
                    "Selecione pelo menos um campo para busca",
                  ),
                  fieldsForLabel: Yup.array(),
                }),
                afterCheckin: Yup.object().shape({
                  fieldsToShow: Yup.array().min(
                    1,
                    "Selecione pelo menos um campo para exibir",
                  ),
                  fieldsToEdit: Yup.array(),
                }),
                endPage: Yup.object().shape({
                  text: Yup.string().required("Campo obrigatório"),
                  timer: Yup.number("Valor inválido").positive(
                    "O valor precisa ser maior que 0",
                  ),
                }),
              }),
              afterCheckin: Yup.object().shape({
                duplicatedCheckin: Yup.string().required("Campo obrigatório"),
                fieldsToShow: Yup.array().min(
                  1,
                  "Selecione pelo menos um campo para exibir",
                ),
                rfid: Yup.object().shape({
                  status: Yup.boolean(),
                }),
                signature: Yup.object().shape({
                  status: Yup.boolean(),
                }),
              }),
              endPage: Yup.object().shape({
                text: Yup.string().required("Campo obrigatório"),
                timer: Yup.number("Valor inválido").positive(
                  "O valor precisa ser maior que 0",
                ),
              }),
              register: Yup.object().shape({
                profilePhotoEnabled: Yup.boolean().default(false),
                personalizationType: Yup.string().oneOf(["simplified", "advanced"]),
                instructions: Yup.object().shape({
                  status: Yup.boolean(),
                  value: Yup.string(),
                }),
                title: Yup.object().shape({
                  status: Yup.boolean(),
                  value: Yup.string(),
                }),
                description: Yup.object().shape({
                  status: Yup.boolean(),
                  value: Yup.string(),
                }),
                paddingBox: Yup.string()
                  .required("Campo obrigatório")
                  .test("paddingBox", "Formato inválido", function (value) {
                    if (!isValidMarginFormat(value)) {
                      return false;
                    } else {
                      return true;
                    }
                  }),
                background: Yup.object().shape({
                  type: Yup.string().required("Campo obrigatório"),
                  color: Yup.string()
                    .required("Campo obrigatório")
                    .test("color", "Valor inválido", function (value) {
                      if (isBackground(value)) {
                        return true;
                      } else {
                        return false;
                      }
                    }),
                  url: Yup.string().test("url", "Campo obrigatório", function (value) {
                    if (this.parent.type === "image") {
                      if (value !== "" && value !== undefined) {
                        return true;
                      } else {
                        return false;
                      }
                    } else {
                      return true;
                    }
                  }),
                }),
                mainLayout: Yup.object().shape({
                  justifyContent: Yup.string().required("Campo obrigatório"),
                  paddingBox: Yup.string()
                    .required("Campo obrigatório")
                    .test("paddingBox", "Formato inválido", function (value) {
                      if (!isValidMarginFormat(value)) {
                        return false;
                      } else {
                        return true;
                      }
                    }),
                }),
                layout: Yup.object().shape({
                  type: Yup.number("Valor inválido").required("Campo obrigatório"),
                  footer: Yup.object().shape({
                    type: Yup.string().required("Campo obrigatório"),
                    url: Yup.string().when("type", {
                      is: "image",
                      then: Yup.string().required("Campo obrigatório"),
                      otherwise: Yup.string(),
                    }),
                    width: Yup.number("Valor inválido").when("type", {
                      is: "image",
                      then: Yup.number("Valor inválido")
                        .typeError("Apenas números")
                        .required("Campo obrigatório")
                        .positive("O valor precisa ser maior que 0"),
                      otherwise: Yup.number("Valor inválido").nullable(),
                    }),
                    socialMedia: Yup.array(),
                  }),
                }),
                logo: Yup.string(),
                logoWidth: Yup.number("Valor inválido")
                  .typeError("Apenas números")
                  .required("Campo obrigatório"),
                fontFamily: Yup.string().required("Campo obrigatório"),
                h5Size: Yup.number("Valor inválido")
                  .typeError("Apenas números")
                  .required("Campo obrigatório")
                  .test("is-decimal", "Apenas valores decimais são permitidos", (value) =>
                    (value + "").match(/^\d+(\.\d{1,2})?$/),
                  ),
                pSize: Yup.number("Valor inválido")
                  .typeError("Apenas números")
                  .required("Campo obrigatório")
                  .test("is-decimal", "Apenas valores decimais são permitidos", (value) =>
                    (value + "").match(/^\d+(\.\d{1,2})?$/),
                  ),
                transparency: Yup.string()
                  .required("Campo obrigatório")
                  .test("transparency", "Valor inválido", function (value) {
                    if (isBackground(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                boxShadow: Yup.string()
                  .required("Campo obrigatório")
                  .test("boxShadow", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                primaryColor: Yup.string()
                  .required("Campo obrigatório")
                  .test("primaryColor", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                primaryColorText: Yup.string()
                  .required("Campo obrigatório")
                  .test("primaryColorText", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                baseColorText: Yup.string()
                  .required("Campo obrigatório")
                  .test("baseColorText", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                bsBorderColor: Yup.string()
                  .required("Campo obrigatório")
                  .test("bsBorderColor", "Valor inválido", function (value) {
                    if (isColor(value)) {
                      return true;
                    } else {
                      return false;
                    }
                  }),
                reset: Yup.boolean(),
                showQRCode: Yup.boolean(),
                showUserData: Yup.boolean(),
              }),
              showQRCode: Yup.object().shape({
                text: Yup.string().max(150, "Máximo de 150 caracteres"),
                link: Yup.string(),
                status: Yup.boolean().required("Campo obrigatório"),
                position: Yup.object().shape({
                  x: Yup.string().required("Campo obrigatório"),
                  y: Yup.string().required("Campo obrigatório"),
                }),
              }),
            });
          } else {
            // When credentialAccess is disabled, return minimal validation
            return Yup.object();
          }
        }),
      }),
      sympla: Yup.object().shape({
        status: Yup.boolean(),
        id: Yup.string().test("id", "Campo obrigatório", function (value) {
          // Use values parameter to check Sympla status
          const symplaStatus = values?.json?.sympla?.status;
          if (symplaStatus) {
            if (value !== "" && value !== undefined) {
              return true;
            } else {
              return false;
            }
          } else {
            return true;
          }
        }),
        authToken: Yup.string().test(
          "authToken",
          "Campo obrigatório",
          function (value) {
            // Use values parameter to check Sympla status
            const symplaStatus = values?.json?.sympla?.status;
            if (symplaStatus) {
              if (value !== "" && value !== undefined) {
                return true;
              } else {
                return false;
              }
            } else {
              return true;
            }
          },
        ),
        last_update: Yup.string(),
        dataSync: Yup.boolean(),
        dataSyncOrigin: Yup.string().test(
          "dataSyncOrigin",
          "Campo obrigatório",
          function (value) {
            // Use values parameter to check Sympla status
            const symplaStatus = values?.json?.sympla?.status;
            if (symplaStatus) {
              if (value !== "" && value !== undefined) {
                return true;
              } else {
                return false;
              }
            } else {
              return true;
            }
          },
        ),
        selectedEventId: Yup.string().test(
          "selectedEventId",
          "É preciso selecionar um evento para continuar",
          function (value) {
            // selectedEventId is required if authToken is set
            const authToken = values?.json?.sympla?.authToken;
            if (authToken && authToken !== "") {
              if (value !== "" && value !== undefined) {
                return true;
              } else {
                return false;
              }
            } else {
              return true;
            }
          },
        ),
        selectedEventName: Yup.string(),
      }),
      networking: Yup.object().shape({
        status: Yup.boolean(),
      }),
      server: networkingStatus ? Yup.object().shape({
        eventosSocketAPI: Yup.string().required("Campo obrigatório"),
        eventosAPI: Yup.string().required("Campo obrigatório"),
        eventosApp: Yup.string().required("Campo obrigatório"),
        imageServer: Yup.string().notRequired(),
      }) : Yup.object(),
      background: networkingStatus ? Yup.object().shape({
        type: Yup.string().required("Campo obrigatório"),
        color: Yup.string()
          .required("Campo obrigatório")
          .test("color", "Valor inválido", function (value) {
            if (isBackground(value)) {
              return true;
            } else {
              return false;
            }
          }),
        url: Yup.string().test("url", "Campo obrigatório", function (value) {
          if (this.parent.type === "image") {
            if (value !== "" && value !== undefined) {
              return true;
            } else {
              return false;
            }
          } else {
            return true;
          }
        }),
      }) : Yup.object(),
      subscriptionLimit: Yup.object().shape({
        status: Yup.boolean().required("Campo obrigatório"),
        limit: Yup.number("Valor inválido")
          .integer()
          .positive("O valor precisa ser maior que 0")
          .nullable()
          .test("limit", "Campo obrigatório", function (value) {
            if (this.parent.status) {
              if (value !== null && value !== undefined) {
                return true;
              } else {
                return false;
              }
            } else {
              return true;
            }
          })
          .test(
            "limit-not-below-registered",
            "O limite deve ser maior ou igual ao total de participantes já registrados",
            function (value) {
              if (!this.parent.status || value === null || value === undefined) {
                return true;
              }
              return Number(value) >= Number(registeredParticipants || 0);
            },
          ),
        disableFormTitle: Yup.string(),
        disableDescription: Yup.string(),
      }),
      login: networkingStatus ? Yup.object().shape({
        loading: Yup.object().shape({
          type: Yup.string().required("Campo obrigatório"),
          url: Yup.string().when("type", {
            is: "image",
            then: Yup.string().required("Campo obrigatório"),
            otherwise: Yup.string(),
          }),
          backgroundColor: Yup.string()
            .required("Campo obrigatório")
            .test("color", "Valor inválido", function (value) {
              if (isBackground(value)) {
                return true;
              } else {
                return false;
              }
            }),
          loaderTime: Yup.number("Valor inválido")
            .positive("O Valor precisa ser maior que 0")
            .min(1000, "O Valor precisa ser maior que 1000"),
          logoWidth: Yup.number("Valor inválido")
            .typeError("Apenas números"),
        }),
        loginType: Yup.number()
          .oneOf([1, 2, 3, 4], "Método de login inválido")
          .required("Campo obrigatório"),
        welcomeTitle: Yup.string().max(150, "Máximo de 150 caracteres"),
        noProfileImage: Yup.boolean(),
        showTutorial: Yup.boolean(),
        enableGuest: Yup.boolean(),
      }) : Yup.object(),
      configs: Yup.object().shape({
        exhibitor: Yup.object().shape({
          enableCollection: Yup.boolean(),
          enableQuizTips: Yup.boolean(),
          quizActivityIds: Yup.array().of(Yup.number()),
        }),
        ranking: Yup.object().shape({
          enableAchievements: Yup.boolean(),
          showAchievements: Yup.boolean(),
          scoreAggregation: Yup.string().oneOf(["all", "activities_only"]),
          score_boost: Yup.object().shape({
            enabled: Yup.boolean(),
            start_at: Yup.string().nullable(),
            end_at: Yup.string().nullable(),
            windows: Yup.array().of(Yup.object().shape({
              start_at: Yup.string().required("Start is required"),
              end_at: Yup.string().required("End is required"),
              multiplier: Yup.number()
                .typeError("Invalid multiplier")
                .min(1, "Minimum multiplier is 1.00")
                .max(5, "Maximum multiplier is 5.00")
                .test("two-decimal-places", "Use at most two decimal places", (value) => (
                  value === undefined || value === null || Math.round(Number(value) * 100) === Number(value) * 100
                )),
            }).test("window-order", "End must be after start", (window) => {
              if (!window?.start_at || !window?.end_at) return true;
              const start = new Date(window.start_at);
              const end = new Date(window.end_at);
              return !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end > start;
            })),
          }),
        }),
        leadCollection: Yup.object().shape({
          showNotRegistered: Yup.boolean(),
        }),
        restaurant: Yup.object().shape({
          banner: Yup.array(),
        }),
        notifications: Yup.object().shape({
          status: Yup.boolean(),
        }),
        networking: Yup.object().shape({
          qrcodeOnly: Yup.boolean(),
        }),
        sections: Yup.object().shape({
          hideMenu: Yup.boolean(),
        }),
        navbar: Yup.object().shape({
          userIconIco: Yup.boolean(),
        }),
        speakers: Yup.object().shape({
          useCompany: Yup.boolean(),
        }),
        qrcode: Yup.object().shape({
          hideUserQR: Yup.boolean(),
        }),
      }),
      home: Yup.object().shape({
        status: Yup.boolean(),
        enableStreamingRow: Yup.boolean(),
        streamings: Yup.object().shape({
          status: Yup.boolean(),
          title: Yup.string().max(100),
          limit: Yup.number().oneOf([6]),
          sort: Yup.string().oneOf(["live_first", "nearest_start"]),
        }),
        activities: Yup.object().shape({
          status: Yup.boolean(),
          title: Yup.string().max(100),
          limit: Yup.number().oneOf([6]),
          sort: Yup.string().oneOf(["recent"]),
        }),
        title: Yup.string(),
        content: Yup.string(),
      }),
      chat: Yup.object().shape({
        enableGroups: Yup.boolean(),
      }),
      banner: Yup.array(),
      sections: Yup.array(
        Yup.object({
          value: Yup.string(),
          label: Yup.string(),
          href: Yup.string(),
          icon: Yup.string(),
          title: Yup.string(),
          displayOnFooter: Yup.boolean(),
        }),
      ),
      maskableIcon: Yup.string(),
      ogImage: Yup.string(),
      metaTagTitle: Yup.string().max(150, "Máximo de 150 caracteres"),
      metaTagDescription: Yup.string()
        .max(255, "Máximo de 255 caracteres")
        .nullable(),
      logo: networkingStatus ? Yup.string().required("Campo obrigatório") : Yup.string(),
      logoNav: networkingStatus ? Yup.string().required("Campo obrigatório") : Yup.string(),
      logoWidth: networkingStatus ? Yup.number("Valor inválido")
        .typeError("Apenas números")
        .required("Campo obrigatório") : Yup.number("Valor inválido").typeError("Apenas números"),
      primaryColor: networkingStatus ? Yup.string()
        .required("Campo obrigatório")
        .test("primaryColor", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }) : Yup.string(),
      primaryColorText: networkingStatus ? Yup.string()
        .required("Campo obrigatório")
        .test("primaryColorText", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }) : Yup.string(),
      secondaryColor: networkingStatus ? Yup.string()
        .required("Campo obrigatório")
        .test("secondaryColor", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }) : Yup.string(),
      secondaryColorText: networkingStatus ? Yup.string()
        .required("Campo obrigatório")
        .test("secondaryColorText", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }) : Yup.string(),
      navPrimaryColor: networkingStatus ? Yup.string()
        .required("Campo obrigatório")
        .test("navPrimaryColor", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }) : Yup.string(),
      navBackgroundColor: networkingStatus ? Yup.string()
        .required("Campo obrigatório")
        .test("navBackgroundColor", "Valor inválido", function (value) {
          if (isBackground(value)) {
            return true;
          } else {
            return false;
          }
        }) : Yup.string(),
      modalBackgroundColor: networkingStatus ? Yup.string()
        .required("Campo obrigatório")
        .test("modalBackgroundColor", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }) : Yup.string(),
      modalColorText: networkingStatus ? Yup.string()
        .required("Campo obrigatório")
        .test("modalColorText", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }) : Yup.string(),
      containerColor: networkingStatus ? Yup.string()
        .required("Campo obrigatório")
        .test("containerColor", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }) : Yup.string(),
      bodyColorText: networkingStatus ? Yup.string()
        .required("Campo obrigatório")
        .test("bodyColorText", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }) : Yup.string(),
      borderColor: networkingStatus ? Yup.string()
        .required("Campo obrigatório")
        .test("borderColor", "Valor inválido", function (value) {
          if (isColor(value)) {
            return true;
          } else {
            return false;
          }
        }) : Yup.string(),
      bodyBackgroundColor: networkingStatus ? Yup.string()
        .required("Campo obrigatório")
        .test("bodyBackgroundColor", "Valor inválido", function (value) {
          if (isBackground(value)) {
            return true;
          } else {
            return false;
          }
        }) : Yup.string(),
      fontFamily: networkingStatus ? Yup.string().required("Campo obrigatório") : Yup.string(),
      fontFamilySize: networkingStatus ? Yup.number("Valor inválido")
        .typeError("Apenas números")
        .required("Campo obrigatório")
        .test("is-decimal", "Apenas valores decimais são permitidos", (value) =>
          (value + "").match(/^\d+(\.\d{1,2})?$/),
        ) : Yup.number(),
      layout: networkingStatus ? Yup.object().shape({
        footer: Yup.object().shape({
          status: Yup.boolean(),
          type: Yup.string().required("Campo obrigatório"),
          url: Yup.string().when("type", {
            is: "image",
            then: Yup.string().required("Campo obrigatório"),
            otherwise: Yup.string(),
          }),
          width: Yup.string().when("type", {
            is: "image",
            then: Yup.string().required("Campo obrigatório"),
            otherwise: Yup.string(),
          }),
          socialMedia: Yup.array(),
        }),
      }) : Yup.object(),
      form: Yup.object().shape({
        title: Yup.object().shape({
          status: Yup.boolean(),
          value: Yup.string(),
        }),
        description: Yup.object().shape({
          status: Yup.boolean(),
          value: Yup.string(),
        }),
        status: Yup.boolean(),
        buttonText: Yup.string()
          .required("Campo obrigatório")
          .max(50, "Máximo de 50 caracteres"),
        validEmails: Yup.object().shape({
          status: Yup.boolean(),
          list: Yup.array().when("status", {
            is: true,
            then: Yup.array()
              .of(Yup.string())
              .min(1, "Adicione pelo menos um domínio permitido"),
            otherwise: Yup.array(),
          }),
        }),
        authorizationMessage: Yup.mixed()
          .nullable()
          .when([], {
            is: (value) => typeof value === "object" && value !== null,
            then: Yup.object().shape({
              status: Yup.boolean().required("Campo obrigatório"),
              value: Yup.string().when("status", {
                is: true,
                then: Yup.string().trim().required("Campo obrigatório"),
                otherwise: Yup.string(),
              }),
            }),
            otherwise: Yup.mixed().notRequired(),
          }),
        register_start_date: Yup.string()
          .required("Campo obrigatório")
          .max(255, "Máximo de 255 caracteres")
          .test("date", "Data inválida", function (valueX) {
            // Only validate if value is different from initial value
            if (valueX === initialValues?.json?.form?.register_start_date) {
              return true;
            }

            if (validateDate(valueX)) {
              return true;
            } else {
              return false;
            }
          })
          .test(
            "date-between-event-dates",
            "Data deve estar entre a data de criação e data de término do evento",
            function (valueX) {
              // Only validate if value is different from initial value
              if (valueX === initialValues?.json?.form?.register_start_date) {
                return true;
              }

              const { creation_date, end_date } = values;

              if (
                creation_date &&
                end_date &&
                valueX &&
                validateDate(creation_date) &&
                validateDate(end_date) &&
                validateDate(valueX)
              ) {
                // Parse dates using dd/MM/yyyy HH:mm format
                const creationDateObj = parse(
                  creation_date,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );
                const endDateObj = parse(
                  end_date,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );
                const valueDateObj = parse(
                  valueX,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );

                if (
                  isValid(creationDateObj) &&
                  isValid(endDateObj) &&
                  isValid(valueDateObj)
                ) {
                  return (
                    (isAfter(valueDateObj, creationDateObj) ||
                      isEqual(valueDateObj, creationDateObj)) &&
                    (isAfter(endDateObj, valueDateObj) ||
                      isEqual(endDateObj, valueDateObj))
                  );
                } else {
                  return false;
                }
              }

              return true;
            },
          ),
        register_end_date: Yup.string()
          .required("Campo obrigatório")
          .max(255, "Máximo de 255 caracteres")
          .test("date", "Data inválida", function (valueX) {
            // Only validate if value is different from initial value
            if (valueX === initialValues?.json?.form?.register_end_date) {
              return true;
            }

            if (validateDate(valueX)) {
              return true;
            } else {
              return false;
            }
          })
          .test("date-after-start", "Data inválida", function (valueX) {
            // Only validate if value is different from initial value
            if (valueX === initialValues?.json?.form?.register_end_date) {
              return true;
            }

            const { register_start_date } = this.parent;
            if (
              register_start_date &&
              valueX &&
              validateDate(register_start_date) &&
              validateDate(valueX)
            ) {
              // Parse dates using dd/MM/yyyy HH:mm format
              const startDateObj = parse(
                register_start_date,
                "dd/MM/yyyy HH:mm",
                new Date(),
              );
              const endDateObj = parse(valueX, "dd/MM/yyyy HH:mm", new Date());

              if (isValid(startDateObj) && isValid(endDateObj)) {
                return isAfter(endDateObj, startDateObj);
              } else {
                return false;
              }
            }
            return true;
          })
          .test(
            "date-between-event-dates",
            "Data deve estar entre a data de criação e data de término do evento",
            function (valueX) {
              // Only validate if value is different from initial value
              if (valueX === initialValues?.json?.form?.register_end_date) {
                return true;
              }

              const { creation_date, end_date } = values;

              if (
                creation_date &&
                end_date &&
                valueX &&
                validateDate(creation_date) &&
                validateDate(end_date) &&
                validateDate(valueX)
              ) {
                // Parse dates using dd/MM/yyyy HH:mm format
                const creationDateObj = parse(
                  creation_date,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );
                const endDateObj = parse(
                  end_date,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );
                const valueDateObj = parse(
                  valueX,
                  "dd/MM/yyyy HH:mm",
                  new Date(),
                );

                if (
                  isValid(creationDateObj) &&
                  isValid(endDateObj) &&
                  isValid(valueDateObj)
                ) {
                  return (
                    (isAfter(valueDateObj, creationDateObj) ||
                      isEqual(valueDateObj, creationDateObj)) &&
                    (isAfter(endDateObj, valueDateObj) ||
                      isEqual(endDateObj, valueDateObj))
                  );
                } else {
                  return false;
                }
              }

              return true;
            },
          ),
        authorizationLink: Yup.string().url("URL inválida"),
        emailLabel: Yup.string()
          .required("Campo obrigatório")
          .max(100, "Máximo de 100 caracteres"),
        showEmailInUserReport: Yup.boolean(),
        fields: Yup.array(),
        fieldsUsers: Yup.array(),
        fieldsToShow: Yup.array(),

        disableFormTitle: Yup.string(),
        disableDescription: Yup.string(),
      }),
    }),
  });
  return schema;
};

export const schemaAddCustomization = () => {
  let schema = {};

  schema = Yup.object().shape({
    evento_id: Yup.number("Valor inválido")
      .integer()
      .required("Campo obrigatório"),
    customizacao_id: Yup.number("Valor inválido")
      .integer()
      .required("Campo obrigatório"),
    titulo: Yup.string()
      .required("Campo obrigatório")
      .max(150, "Máximo de 150 caracteres"),
    json: Yup.object().required("Campo obrigatório"),
    data_inicio: Yup.string().required("Campo obrigatório"),
    data_termino: Yup.string().required("Campo obrigatório"),
    status: Yup.number("Valor inválido")
      .required("Campo obrigatório")
      .positive()
      .integer(),
  });

  return schema;
};

export const schemaAddForm = () => {
  let schema = {};
  schema = Yup.object().shape({
    inputID: Yup.number("Valor inválido")
      .integer()
      .required("Campo obrigatório"),
    label: Yup.string()
      .required("Campo obrigatório")
      .max(100, "Máximo de 100 caracteres"),
    subLabel: Yup.string().max(75, "Máximo de 75 caracteres"),
    type: Yup.string().required("Campo obrigatório"),
    validation: Yup.string().required("Campo obrigatório"),
    defaultValue: Yup.string(),
    placeholder: Yup.string(),
    col: Yup.number("Valor inválido").required("Campo obrigatório"),
    primary: Yup.boolean().required("Campo obrigatório"),
    options: Yup.array()
      .of(Yup.string())
      .required("Campo obrigatório")
      .max(500, "Máximo de 500 opções")
      .test(
        "options-required-for-select-checkbox",
        "Pelo menos uma opção é obrigatória para campos de seleção ou checkbox",
        function (value) {
          const { type } = this.parent;
          if (type === "select" || type === "checkbox") {
            if (
              !value ||
              value.length === 0 ||
              (value.length === 1 && value[0] === "")
            ) {
              return false;
            }
            const validOptions = value.filter(
              (option) => option && option.trim() !== "",
            );
            if (validOptions.length === 0) return false;

            const normalizedOptions = validOptions.map((option) => option.trim());
            return new Set(normalizedOptions).size === normalizedOptions.length;
          }
          return true;
        },
      ),
    required: Yup.boolean().required("Campo obrigatório"),
    allowMultipleChoice: Yup.boolean(),
    allowMultipleChoiceMax: Yup.number("Valor inválido").when(
      "allowMultipleChoice",
      {
        is: true,
        then: Yup.number("Valor inválido")
          .integer("Deve ser um número inteiro")
          .positive("Deve ser maior que 0")
          .required("Campo obrigatório quando múltipla escolha está ativada")
          .test(
            "max-greater-than-min",
            "Máximo deve ser maior ou igual ao mínimo",
            function (value) {
              const { allowMultipleChoiceMin } = this.parent;
              if (allowMultipleChoiceMin && value) {
                return value >= allowMultipleChoiceMin;
              }
              return true;
            },
          ),
        otherwise: Yup.number("Valor inválido"),
      },
    ),
    allowMultipleChoiceMin: Yup.number("Valor inválido").when(
      "allowMultipleChoice",
      {
        is: true,
        then: Yup.number("Valor inválido")
          .integer("Deve ser um número inteiro")
          .positive("Deve ser maior que 0")
          .required("Campo obrigatório quando múltipla escolha está ativada")
          .test(
            "min-less-than-max",
            "Mínimo deve ser menor ou igual ao máximo",
            function (value) {
              const { allowMultipleChoiceMax } = this.parent;
              if (allowMultipleChoiceMax && value) {
                return value <= allowMultipleChoiceMax;
              }
              return true;
            },
          ),
        otherwise: Yup.number("Valor inválido"),
      },
    ),
    vacancyEnabled: Yup.boolean()
      .required("Campo obrigatório")
      .test(
        "vacancy-only-for-select",
        "O sistema de vagas está disponível apenas para campos de seleção",
        function (value) {
          return this.parent.type === "select" || value !== true;
        },
      ),
    vacancyOptions: Yup.array()
      .of(
        Yup.object().shape({
          option: Yup.string().required("Opção obrigatória"),
          totalAvailable: Yup.number("Valor inválido")
            .typeError("Informe um número inteiro")
            .integer("Deve ser um número inteiro")
            .min(0, "Deve ser maior ou igual a 0")
            .test(
              "vacancy-total-not-below-associated",
              "O total disponível não pode ser menor que o total de associados",
              function (value) {
                const associatedTotal = Number(
                  this.parent.associatedTotal ?? this.parent.used ?? 0,
                );
                return Number(value) >= associatedTotal;
              },
            )
            .required("Campo obrigatório"),
        }),
      )
      .test(
        "vacancy-options-match-field-options",
        "Defina a disponibilidade para todas as opções do campo",
        function (value) {
          const { type, vacancyEnabled, options } = this.parent;
          if (!vacancyEnabled) return true;
          if (type !== "select") return false;

          const fieldOptions = (Array.isArray(options) ? options : [])
            .map((option) => String(option || "").trim())
            .filter((option, index, allOptions) => option && allOptions.indexOf(option) === index);
          const configuredOptions = (Array.isArray(value) ? value : []).map((row) => row.option);

          return fieldOptions.length === configuredOptions.length
            && fieldOptions.every((option) => configuredOptions.includes(option));
        },
      ),
    dependencies: Yup.array()
      .of(
        Yup.object().shape({
          inputID: Yup.string().required("Campo obrigatório"),
          value: Yup.mixed().when("inputID", {
            is: (inputID) => !!inputID, // Check if inputID is truthy
            then: Yup.mixed()
              .required("Campo obrigatório")
              .typeError("Campo obrigatório"),
            otherwise: Yup.mixed(),
          }),
        }),
      )
      .test(
        "no-empty-dependencies",
        "Remova dependências vazias ou selecione um campo",
        function (dependencies) {
          if (!dependencies || dependencies.length === 0) {
            return true; // Empty array is valid
          }

          // Check if any dependency has empty inputID
          const hasEmptyInputID = dependencies.some(
            (dep) => !dep.inputID || dep.inputID === "",
          );

          if (hasEmptyInputID) {
            return this.createError({
              message: "Todas as dependências devem ter um campo selecionado",
              path: this.path,
            });
          }

          return true;
        },
      ),
    displayField: Yup.boolean(),
    blockEdit: Yup.boolean().required("Campo obrigatório"),
    blockEditMessage: Yup.string().max(75, "Máximo de 75 caracteres"),
    fieldToShow: Yup.boolean().required("Campo obrigatório"),
    _id: Yup.string(),
    hideFieldOnRegister: Yup.boolean().required("Campo obrigatório"),
    fieldWithUppercase: Yup.boolean().required("Campo obrigatório"),
    showInUserReport: Yup.boolean().required("Campo obrigatório"),
    rfigtag: Yup.boolean().required("Campo obrigatório"),
    whatsapp: Yup.boolean().default(false),
  });

  schema = schema.test(
    "supported-field-validation",
    "Invalid field validation",
    (values) => {
      if (!values) return true;

      const supportedValidations = {
        input: ["text", "textBig", "number", "email", "telefone", "cpf", "cnpj", "date"],
        select: ["text"],
        checkbox: ["checkbox"],
        textarea: ["text", "textBig"],
      };

      return supportedValidations[values.type]?.includes(values.validation) ?? false;
    },
  );

  return schema;
};

export const schemaAddSocialMedia = () => {
  let schema = {};

  schema = Yup.object().shape({
    name: Yup.string().required("Campo obrigatório"),
    url: Yup.string().required("Campo obrigatório"),
  });

  return schema;
};

export const schemaAddGallery = () => {
  let schema = {};

  schema = Yup.object().shape({
    image: Yup.string().required("Campo obrigatório"),
    width: Yup.number("Valor inválido")
      .typeError("Apenas números")
      .required("Campo obrigatório"),
    height: Yup.number("Valor inválido")
      .typeError("Apenas números")
      .required("Campo obrigatório"),
  });

  return schema;
};

export const schemaAddExtraSection = () => {
  let schema = {};

  schema = Yup.object().shape({
    value: Yup.string().required("Campo obrigatório"),
    label: Yup.string().required("Campo obrigatório"),
    href: Yup.string().required("Campo obrigatório"),
    icon: Yup.string().required("Campo obrigatório"),
    title: Yup.string().required("Campo obrigatório"),
    displayOnFooter: Yup.boolean().required("Campo obrigatório"),
  });
  return schema;
};

// background: {
//   type: "color",
//     url: "",
//       color: { backgroundColor },
// },
// mainLayout: {
//   justifyContent: "center",
//     paddingBox: "1.50 1.50 1.50 1.50",
//         },
// paddingBox: "1.50 1.50 1.50 1.50",
//   transparency: "rgba(255,255,255,1)",
//     boxShadow: "rgba(0, 0, 0, 1)",
//       primaryColor: { buttonColor },
// primaryColorText: { backgroundColor },
// baseColorText: "rgba(109, 108, 108, 255)",
// bsBorderColor: "rgb(206, 212, 218)",

// background: {
//   type: "color",
//     url: "",
//       color: { backgroundColor },
// },
// mainLayout: {
//   justifyContent: "center",
//     paddingBox: "1.50 1.50 1.50 1.50",
//         },
// paddingBox: "0.00 0.00 0.00 0.00",
//   transparency: "rgba(255,255,255,0)",
//     boxShadow: "rgba(0, 0, 0, 0)",
//       primaryColor: { buttonColor },
// primaryColorText: { BlackOrWhiteDependentOfDefinedColor },
// baseColorText: "rgba(109, 108, 108, 255)",
// bsBorderColor: "rgb(206, 212, 218)",

export const jsonEvent = (platform) => {
  let data = "";

  switch (platform) {
    case 1:
      data = {
        personalizationType: "simplified",
        simplifiedThemeMode: "light",
        background: {
          type: "color",
          url: "",
          color: "rgba(255,255,255,1)",
        },
        mainLayout: {
          justifyContent: "center",
          paddingBox: "1.50 1.50 1.50 1.50",
        },
        layout: {
          type: 1, //1 or 2X
          footer: {
            type: "noFooter", //icons or image
            url: "",
            width: 275,
            socialMedia: [], //facebook,instagram,youtube,linkedin,twitter,whatsapp,website
          },
        },
        subscriptionLimit: {
          status: false,
          limit: 300,
          disableFormTitle: "Limite de inscrições atingido!",
          disableDescription: `Agradecemos pelo seu interesse!`,
        },
        noCache: false,
        logo: "",
        maskableIcon: "",
        ogImage: "",
        metaTagTitle: "APLICATIVOS - R1 EvenTech",
        metaTagDescription: "Plataforma de aplicativos da R1 EvenTech.",
        logoWidth: 225,
        fontFamily: "Roboto",
        h5Size: 1.25,
        pSize: 1,
        paddingBox: "0.00 0.00 0.00 0.00",
        transparency: "rgba(255,255,255,0)",
        boxShadow: "rgba(0, 0, 0, 0)",
        primaryColor: "rgb(215, 205, 18)",
        primaryColorText: "#000000",
        baseColorText: "rgba(109, 108, 108, 255)",
        presenterBackground: "#ffffff",
        bsBorderColor: "rgb(206, 212, 218)",
        applications: {
          status: true,
          title: {
            status: true,
            value: "Central de aplicativos",
          },
          description: {
            status: true,
            value: "Selecione um aplicativo para continuar",
          },
          reset: false,
        },
        end: {
          reset: false,
          showQRCode: false,
          showUserData: false,
          title: {
            status: true,
            value: "Confirmação de Inscrição",
          },
          description: {
            status: true,
            value: `Parabéns! Sua inscrição foi realizada com sucesso!`,
          },
        },
        form: {
          title: {
            status: true,
            value: "Formulário de inscrição",
          },
          description: {
            status: true,
            value: "Preencha os campos abaixo para se inscrever",
          },
          buttonText: "Continuar",
          validEmails: {
            status: false,
            list: [],
          },
          authorizationMessage: {
            status: true,
            value:
              "Autorizo a coleta dos meus dados pessoais de acordo com os termos",
          },
          status: true,
          register_start_date: defaultCreationDate(),
          register_end_date: defaultEndDate(),
          authorizationLink: "https://r1grupo.com.br/privacidade/",
          fieldsToShow: [837312],
          fieldsUsers: [],
          fields: [
            {
              inputID: 837312,
              label: "Nome",
              subLabel: "",
              type: "input",
              validation: "text",
              defaultValue: "",
              placeholder: "",
              col: 12,
              primary: false,
              options: [],
              required: true,
              allowMultipleChoice: false,
              allowMultipleChoiceMax: 1,
              allowMultipleChoiceMin: 1,
              dependencies: [],
              displayField: true,
              blockEdit: false,
              blockEditMessage: "",
              fieldToShow: true,
              _id: "",
              hideFieldOnRegister: false,
              fieldWithUppercase: false,
              showInUserReport: true,
              rfigtag: false,
              vacancyEnabled: false,
              vacancyOptions: []
            }
          ],
          disableFormTitle: "Período de inscrição encerrado!",
          disableDescription: `Agradecemos pelo seu interesse!`,
        },
      };
      return data;
    case 2:
      data = {
        personalizationType: "simplified",
        simplifiedThemeMode: "light",
        server: {
          eventosSocketAPI: domainConfig.eventosSocketAPI,
          eventosAPI: domainConfig.eventosAPI,
          eventosApp: domainConfig.eventos,
          imageServer: domainConfig.imageServer,
        },
        email: {
          register: {
            status: false,
            template_id: null,
            template_version: null,
            templates: {
              "0": {
                template_id: null,
                template_version: null,
              },
              "1": {
                template_id: null,
                template_version: null,
              },
              "2": {
                template_id: null,
                template_version: null,
              },
            },
          },
        },
        credentialAccess: {
          status: false,
          loginCode: generatePassword(6),
          monitorCode: String(generateSixDigitCode()).padStart(6, "0"),
          checkinEdit: true,
          newRegister: false,
          removeEmail: false,
          noCache: false,
          zplModels: [
            {
              name: "Padrão",
              template: `^XA
^FX 300dpi, 90x30mm, 12dot/mm

^FX Meio de transferencia- D (térmico direto) ou T (transferência térmica- Ribbon)^FS
^MTT^FS
^FX largura da etiqueta, em pontos. 90mm, 1080dots^FS
^PW1080^FS
^FX comprimento da etiqueta, em pontos. 30mm, 360dots^FS
^ML360^FS
^FX escuridão
~SD15^FS
^MD30^FS
^FX velocidade de impressão
^PR2^FS

^CI28

^FX nome para impressão 
^CF0,100^FS
^FO15,50^FB1050,3,3,C^FD{{id_campo}}\\&^FS

^XZ`,
            },
          ],
          apresentation: {
            personalizationType: "simplified",
            simplifiedThemeMode: "light",
            title: {
              status: true,
              value: "Confirme sua presença",
            },
            description: {
              status: true,
              value: "Clique no botão abaixo para prosseguir com seu Check-in",
            },
            logo: "",
            logoNav: "",
            maskableIcon: "",
            ogImage: "",
            metaTagTitle: "CREDENCIAMENTO - R1 EvenTech",
            metaTagDescription: "Sistema de credenciamento para eventos. Cadastre-se e acesse com facilidade.",
            logoWidth: 250,
            imageSide: {
              value: 0,
              position: "start",
              background: "",
            },
            textSide: {
              background: {
                type: "color",
                url: "",
                color: "rgba(255,255,255,1)",
              },
              value: 100,
              fontFamily: "Roboto",
              primaryColor: "#000",
              primaryColorText: "rgba(255,255,255,1)",
              bodyColorText: "rgba(90, 90, 90, 1)",
              borderColor: "rgb(206, 212, 218)",
              bodyBackgroundColor: "rgba(255,255,255,1)",
              containerColor: "#000",
              secondaryColor: "rgb(108, 117, 125)",
              secondaryColorText: "rgba(255, 255, 255, 1)",
              navBackgroundColor: "rgba(255, 255, 255, 1)",
              navPrimaryColor: "#000",
              modalBackgroundColor: "rgba(255, 255, 255, 1)",
              modalColorText: "rgba(90, 90, 90, 1)",
              h5: 1.75,
              p: 1.5,
            },
            checkin: {
              textSelect: "Digite o seu nome",
              textScan: "Aproxime o seu QR Code",
              type: 3,
              initialType: 1,
              "fieldsForLabel": [
                {
                  "label": "Nome para credencial",
                  "value": "Nome para credencial",
                  "inputID": 878413
                },
                {
                  "label": "Empresa",
                  "value": "Empresa",
                  "inputID": 744631
                }
              ],
              "fieldsToSearch": [
                {
                  "label": "Nome para credencial",
                  "value": "Nome para credencial",
                  "inputID": 878413
                },
                {
                  "label": "E-mail",
                  "value": "Email",
                  "inputID": "email"
                }
              ]
            },
            afterCheckin: {
              duplicatedCheckin:
                "Participante já efetuou Check-in anteriormente. Para prosseguir, clique no botão abaixo",
              fieldsToShow: [
                {
                  "label": "Nome para credencial",
                  "value": "Nome para credencial",
                  "inputID": 878413
                },
                {
                  "label": "Empresa",
                  "value": "Empresa",
                  "inputID": 744631
                }
              ],
              rfid: {
                status: true
              },
              signature: {
                status: false
              },
            },
            registrationUpdate: {
              checkin: {
                textSelect: "Digite o seu nome",
                textScan: "Aproxime o seu QR Code",
                type: 3,
                initialType: 1,
                "fieldsForLabel": [
                  {
                    "label": "Nome para credencial",
                    "value": "Nome para credencial",
                    "inputID": 878413
                  },
                  {
                    "label": "Empresa",
                    "value": "Empresa",
                    "inputID": 744631
                  }
                ],
                "fieldsToSearch": [
                  {
                    "label": "Nome para credencial",
                    "value": "Nome para credencial",
                    "inputID": 878413
                  },
                  {
                    "label": "E-mail",
                    "value": "Email",
                    "inputID": "email"
                  }
                ]
              },
              afterCheckin: {
                fieldsToShow: [
                  {
                    "label": "Nome para credencial",
                    "value": "Nome para credencial",
                    "inputID": 878413
                  },
                  {
                    "label": "Empresa",
                    "value": "Empresa",
                    "inputID": 744631
                  }
                ],
                fieldsToEdit: []
              },
              endPage: {
                text: "Obrigado por participar do evento",
                timer: 10,
              },
            },
            endPage: {
              text: "Obrigado por participar do evento",
              timer: 10,
            },
            register: {
              profilePhotoEnabled: false,
              personalizationType: "simplified",
              instructions: {
                status: true,
                value: "Preencha os campos abaixo para se inscrever",
              },
              title: {
                status: true,
                value: "Confirmação de Inscrição",
              },
              description: {
                status: true,
                value: `Parabéns! Sua inscrição foi realizada com sucesso!`,
              },
              background: { type: "color", url: "", color: "rgba(255,255,255,1)" },
              mainLayout: { justifyContent: "center", paddingBox: "1.50 1.50 1.50 1.50" },
              layout: { type: 1, footer: { type: "noFooter", url: "", width: 275, socialMedia: [] } },
              logo: "",
              logoWidth: 225,
              fontFamily: "Roboto",
              h5Size: 1.25,
              pSize: 1,
              paddingBox: "0.00 0.00 0.00 0.00",
              transparency: "rgba(255,255,255,0)",
              boxShadow: "rgba(0, 0, 0, 0)",
              primaryColor: "rgb(215, 205, 18)",
              primaryColorText: "#000000",
              baseColorText: "rgba(90, 90, 90, 1)",
              bsBorderColor: "rgb(206, 212, 218)",
              reset: false,
              showQRCode: true,
              showUserData: true,
            },
            showQRCode: {
              text: "Leia o QRCode para participar",
              link: "",
              status: false,
              position: {
                x: "left",
                y: "bottom",
              },
            },
          },
        },
        sympla: {
          status: false,
          id: "",
          authToken: "",
          last_update: "",
          dataSync: false,
          dataSyncOrigin: "sympla",
          selectedEventId: "",
          selectedEventName: "",
        },
        networking: {
          status: false,
        },
        background: {
          type: "color",
          url: "",
          color: "rgba(246,248,251,255)",
        },
        subscriptionLimit: {
          status: false,
          limit: 300,
          disableFormTitle: "Limite de inscrições atingido!",
          disableDescription: `Agradecemos pelo seu interesse!`,
        },
        login: {
          loading: {
            type: "image",
            url: "",
            backgroundColor: "rgba(255,255,255,1)",
            loaderTime: 2000,
            logoWidth: 200,
          },
          welcomeTitle: "Bem-vindo ao nosso evento!",
          loginType: 1,
          noProfileImage: false,
          showTutorial: true,
          enableGuest: false,
        },
        configs: {
          exhibitor: {
            enableCollection: false,
            quizActivityIds: [],
          },
          ranking: {
            enableAchievements: true,
            showAchievements: true,
            scoreAggregation: "all",
            score_boost: {
              enabled: false,
              start_at: null,
              end_at: null,
              windows: [],
            },
          },
          restaurant: {
            banner: [],
          },
          leadCollection: {
            showNotRegistered: true,
          },
          notifications: {
            status: false,
          },
          networking: {
            qrcodeOnly: false,
          },
          sections: {
            hideMenu: false,
          },
          navbar: {
            userIconIco: false,
          },
          speakers: {
            useCompany: false,
          },
          qrcode: {
            hideUserQR: false,
          },
        },
        home: {
          status: false,
          enableStreamingRow: true,
          streamings: {
            status: true,
            title: "Transmissões",
            limit: 6,
            sort: "live_first",
          },
          activities: {
            status: false,
            title: "Atividades em destaque",
            limit: 6,
            sort: "recent",
          },
          title: "",
          content: "",
        },
        banner: [],
        "sections": [
          {
            "href": "/",
            "icon": "faHome",
            "label": "Página inicial",
            "title": "Página inicial",
            "value": "pagina-inicial",
            "displayOnFooter": true
          },
          {
            "value": "agenda",
            "label": "Agenda",
            "href": "/agenda",
            "icon": "faCalendarDays",
            "title": "Agenda",
            "displayOnFooter": true,
            "external_link": false,
            "url": "",
            "htmlContent": "",
            "content": {
              "type": "cardWithImage",
              "image": "",
              "location": "",
              "link": ""
            }
          },
          {
            "value": "palestrantes",
            "label": "Palestrantes",
            "href": "/palestrantes",
            "icon": "faChalkboardUser",
            "title": "Palestrantes",
            "displayOnFooter": false,
            "external_link": false,
            "url": "",
            "htmlContent": "",
            "content": {
              "type": "cardWithImage",
              "image": "",
              "location": "",
              "link": ""
            }
          },
          {
            "value": "expositores",
            "label": "Expositores",
            "href": "/expositores",
            "icon": "faStore",
            "title": "Expositores",
            "displayOnFooter": false,
            "external_link": false,
            "url": "",
            "htmlContent": "",
            "content": {
              "type": "cardWithImage",
              "image": "",
              "location": "",
              "link": ""
            }
          },
          {
            "value": "participantes",
            "label": "Networking",
            "href": "/participantes",
            "icon": "faUserGroup",
            "title": "Networking",
            "displayOnFooter": true,
            "external_link": false,
            "url": "",
            "htmlContent": "",
            "content": {
              "type": "cardWithImage",
              "image": "",
              "location": "",
              "link": ""
            }
          },
          {
            "value": "coleta-de-lead",
            "label": "Coleta de lead",
            "href": "/coleta-de-lead",
            "icon": "faAddressCard",
            "title": "Coleta de lead",
            "displayOnFooter": false,
            "external_link": false,
            "url": "",
            "htmlContent": "",
            "content": {
              "type": "cardWithImage",
              "image": "",
              "location": "",
              "link": ""
            }
          },
          {
            "value": "galeria",
            "label": "Galeria",
            "href": "/galeria",
            "icon": "faImages",
            "title": "Galeria",
            "displayOnFooter": false,
            "external_link": false,
            "url": "",
            "htmlContent": "",
            "content": {
              "type": "cardWithImage",
              "image": "",
              "location": "",
              "link": ""
            }
          },
          {
            "value": "ranking",
            "label": "Ranking",
            "href": "/ranking",
            "icon": "faRankingStar",
            "title": "Ranking",
            "displayOnFooter": false,
            "external_link": false,
            "url": "",
            "htmlContent": "",
            "content": {
              "type": "cardWithImage",
              "image": "",
              "location": "",
              "link": ""
            }
          },
          {
            "value": "atividades",
            "label": "Atividades",
            "href": "/atividades",
            "icon": "faCrown",
            "title": "Atividades",
            "displayOnFooter": false,
            "external_link": false,
            "url": "",
            "htmlContent": "",
            "content": {
              "type": "cardWithImage",
              "image": "",
              "location": "",
              "link": ""
            }
          },
          createSocialSection()
        ],
        logo: "",
        maskableIcon: "",
        ogImage: "",
        metaTagTitle: "EVENTOS - R1 EvenTech",
        metaTagDescription: "Plataforma de eventos da R1 EvenTech.",
        logoWidth: 275,
        logoNav: "",
        primaryColor: "rgb(215, 205, 18)",
        primaryColorText: "#000000",
        secondaryColor: "rgb(108, 117, 125)",
        secondaryColorText: "rgba(255, 255, 255, 1)",
        navPrimaryColor: "#000",
        navBackgroundColor: "rgba(255, 255, 255, 1)",
        modalBackgroundColor: "rgba(255, 255, 255, 1)",
        modalColorText: "rgba(90, 90, 90, 1)",
        containerColor: "rgba(90, 90, 90, 1)",
        bodyColorText: "rgba(90, 90, 90, 1)",
        borderColor: "rgb(206, 212, 218)",
        bodyBackgroundColor: "rgba(255,255,255,1)",
        fontFamily: "Roboto",
        fontFamilySize: 1,
        layout: {
          footer: {
            status: false,
            type: "noFooter",
            url: "",
            width: 275,
            socialMedia: [],
          },
        },
        form: {
          title: {
            status: true,
            value: "Crie seu perfil",
          },
          description: {
            status: true,
            value: "Preencha os campos abaixo para acessar o aplicativo",
          },
          buttonText: "Continuar",
          authorizationMessage: {
            status: true,
            value:
              "Autorizo a coleta dos meus dados pessoais de acordo com os termos",
          },
          status: true,
          register_start_date: defaultCreationDate(),
          register_end_date: defaultEndDate(),
          authorizationLink: "https://r1grupo.com.br/privacidade/",
          emailLabel: "E-mail",
          showEmailInUserReport: true,
          fieldsToShow: [878413, 744631],
          fieldsUsers: [
            {
              "inputID": 118488,
              "label": "Nome completo",
              "subLabel": "",
              "type": "input",
              "validation": "text",
              "defaultValue": "",
              "placeholder": "",
              "col": 12,
              "primary": false,
              "options": [],
              "required": true,
              "allowMultipleChoice": false,
              "allowMultipleChoiceMax": 1,
              "allowMultipleChoiceMin": 1,
              "dependencies": [],
              "displayField": true,
              "blockEdit": false,
              "blockEditMessage": "",
              "fieldToShow": true,
              "_id": "",
              "hideFieldOnRegister": false,
              "fieldWithUppercase": false,
              "showInUserReport": true,
              "rfigtag": false,
              "vacancyEnabled": false,
              "vacancyOptions": []
            }
          ],
          fields: [
            {
              "inputID": 578214,
              "label": "Nome completo",
              "subLabel": "",
              "type": "input",
              "validation": "text",
              "defaultValue": "",
              "placeholder": "",
              "col": 12,
              "primary": false,
              "options": [],
              "required": true,
              "allowMultipleChoice": false,
              "allowMultipleChoiceMax": 1,
              "allowMultipleChoiceMin": 1,
              "dependencies": [],
              "displayField": true,
              "blockEdit": false,
              "blockEditMessage": "",
              "fieldToShow": false,
              "_id": "",
              "hideFieldOnRegister": false,
              "fieldWithUppercase": false,
              "showInUserReport": true,
              "rfigtag": false,
              "vacancyEnabled": false,
              "vacancyOptions": []
            },
            {
              "inputID": 878413,
              "label": "Nome para credencial",
              "subLabel": "",
              "type": "input",
              "validation": "text",
              "defaultValue": "",
              "placeholder": "",
              "col": 12,
              "primary": false,
              "options": [],
              "required": true,
              "allowMultipleChoice": false,
              "allowMultipleChoiceMax": 1,
              "allowMultipleChoiceMin": 1,
              "dependencies": [],
              "displayField": true,
              "blockEdit": false,
              "blockEditMessage": "",
              "fieldToShow": true,
              "_id": "",
              "hideFieldOnRegister": false,
              "fieldWithUppercase": false,
              "showInUserReport": true,
              "rfigtag": false,
              "vacancyEnabled": false,
              "vacancyOptions": []
            },
            {
              "inputID": 744631,
              "label": "Empresa",
              "subLabel": "",
              "type": "input",
              "validation": "text",
              "defaultValue": "",
              "placeholder": "",
              "col": 12,
              "primary": false,
              "options": [],
              "required": true,
              "allowMultipleChoice": false,
              "allowMultipleChoiceMax": 1,
              "allowMultipleChoiceMin": 1,
              "dependencies": [],
              "displayField": true,
              "blockEdit": false,
              "blockEditMessage": "",
              "fieldToShow": true,
              "_id": "",
              "hideFieldOnRegister": false,
              "fieldWithUppercase": false,
              "showInUserReport": true,
              "rfigtag": false,
              "vacancyEnabled": false,
              "vacancyOptions": []
            },
            {
              "inputID": 883877,
              "label": "Telefone",
              "subLabel": "",
              "type": "input",
              "validation": "telefone",
              "defaultValue": "",
              "placeholder": "",
              "col": 12,
              "primary": false,
              "options": [],
              "required": true,
              "allowMultipleChoice": false,
              "allowMultipleChoiceMax": 1,
              "allowMultipleChoiceMin": 1,
              "dependencies": [],
              "displayField": true,
              "blockEdit": false,
              "blockEditMessage": "",
              "fieldToShow": false,
              "_id": "",
              "hideFieldOnRegister": false,
              "fieldWithUppercase": false,
              "showInUserReport": true,
              "rfigtag": false,
              "vacancyEnabled": false,
              "vacancyOptions": []
            },
            {
              "inputID": 552783,
              "label": "RFID",
              "subLabel": "",
              "type": "input",
              "validation": "text",
              "defaultValue": "",
              "placeholder": "",
              "col": 12,
              "primary": false,
              "options": [],
              "required": true,
              "allowMultipleChoice": false,
              "allowMultipleChoiceMax": 1,
              "allowMultipleChoiceMin": 1,
              "dependencies": [],
              "displayField": true,
              "blockEdit": false,
              "blockEditMessage": "",
              "fieldToShow": false,
              "_id": "",
              "hideFieldOnRegister": true,
              "fieldWithUppercase": false,
              "showInUserReport": false,
              "rfigtag": true,
              "vacancyEnabled": false,
              "vacancyOptions": []
            }
          ],
          disableFormTitle: "Período de inscrição encerrado!",
          disableDescription: `Agradecemos pelo seu interesse!`,
        },
      };
      return data;
    default:
      return "";
  }
};
